import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Camera, CheckCircle2, Clock, FileText, Loader2, MessageSquare, Navigation, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import FullScreenPdfViewer from '../components/FullScreenPdfViewer';
import QuestionnaireForm from '../components/QuestionnaireForm';
import SearchableSelect from '../components/SearchableSelect';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import { useAssessableFacilities } from '../hooks/useAssessibleFacilities';
import { useCreateAssessment, useUpdateRecommendationResolved } from '../hooks/useAssessments';
import { getCurrentUser } from '../hooks/useAuth';
import { useCameraCapture } from '../hooks/useCameraCapture';
import { useCreateFacility } from '../hooks/useFacilities';

import {
  assessmentsApi,
  complianceApi,
  facilityTypesApi,
  questionnairesApi,
  questionsApi,
  usersApi,
} from '../lib/api-client';

import { canCreateAssessment } from '../lib/permissions';
import {
  allowedComplianceResponseQuestionIds,
  compareComplianceQuestionOrder,
  compareSectionOrder,
  isComplianceQuestionTreeVisible,
  mergeFlatComplianceQuestionsIntoTree,
  normalizeComplianceQuestionTree,
  normalizeQuestionnaireSections,
  collectComplianceQuestionTreeIds,
} from '../lib/complianceQuestionnaireNormalize';
import type {
  AnswerDto,
  AppealResolutionDto,
  AssessmentType,
  ComplianceQuestionTreeDto,
  ComplianceResponseItemDto,
  CreateAssessmentDto,
  CreateFacilityDto,
  FacilityTypeDto,
  FinalDecision,
  QuestionnaireDocumentDto,
  QuestionnaireDto,
  QuestionnaireSectionDto,
  RecommendationItemDto,
  UserRole,
} from '../types/api';
import FacilityTypeSelector from '../components/FacilityTypeSelector';

// Requirement types based on facility type
enum RequirementType {
  STANDARD_TOILET = 'STANDARD_TOILET',
  STANDARD_HOUSE = 'STANDARD_HOUSE',
  HANDWASHING_FACILITY = 'HANDWASHING_FACILITY',
  CLEAN_WATER_FACILITY = 'CLEAN_WATER_FACILITY',
  WASTE_MANAGEMENT_FACILITY = 'WASTE_MANAGEMENT_FACILITY',
}

interface UploadedImage {
  id: string;
  url: string;
  filename: string;
}

const DECISION_OPTIONS: { value: FinalDecision; labelKey: string; homeOnly?: boolean }[] = [
  { value: 'NO_ACTION', labelKey: 'new_assessment.no_action_required' },
  { value: 'WARNING', labelKey: 'new_assessment.issue_warning' },
  { value: 'FINES', labelKey: 'new_assessment.impose_fines' },
  { value: 'RELOCATION_OF_THE_HOUSEHOLD', labelKey: 'new_assessment.relocation_household', homeOnly: true },
  { value: 'TEMPORARY_CLOSING', labelKey: 'new_assessment.temporary_closure' },
  { value: 'PERMANENT_CLOSING_OF_THE_FACILITY', labelKey: 'new_assessment.permanent_closure' },
];

// Tolerates spaces around the comma — stored facility coordinates aren't always normalized.
const COORDINATES_RE = /^\s*-?\d+\.?\d*\s*,\s*-?\d+\.?\d*\s*$/;

const assessmentSchema = z
  .object({
    facilityId: z.string().min(1, 'Please select a facility'),
    coordinates: z.string().regex(COORDINATES_RE, 'Coordinates must be in format: lat,lng').optional().or(z.literal('')),
    questionnaireId: z.string().optional(),
    finalDecision: z.enum(['NO_ACTION', 'FINES', 'CLOSURE', 'WARNING', 'RELOCATION_OF_THE_HOUSEHOLD', 'TEMPORARY_CLOSING', 'PERMANENT_CLOSING_OF_THE_FACILITY']),
    // valueAsNumber turns an empty input into NaN, and a JSON round-trip (draft
    // restore) turns NaN into null — both must count as "not provided", not fail.
    chargedAmount: z.preprocess(
      v => (v === null || v === '' || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v),
      z.number().optional()
    ),
    answers: z.array(
      z.object({
        questionId: z.string(),
        selectedOptionId: z.string(),
      })
    ),
    requirements: z.array(
      z.object({
        requirementType: z.enum([
          'STANDARD_TOILET',
          'STANDARD_HOUSE',
          'HANDWASHING_FACILITY',
          'CLEAN_WATER_FACILITY',
          'WASTE_MANAGEMENT_FACILITY',
        ]),
        isAvailable: z.boolean(),
        hasLand: z.boolean().optional(),
        hasFunds: z.boolean().optional(),
        estimatedCompletion: z.string().optional(),
        notes: z.string().optional(),
      })
    ),
    photos: z.array(z.string()),
    committee: z.array(z.string()).optional(),
    additionalComment: z.string().optional(),
    followupDate: z.string().optional(),
    isFlagged: z.boolean().optional(),
  })
  .refine(
    data => {
      // If finalDecision is FINES, chargedAmount must be provided and greater than 0
      if (data.finalDecision === 'FINES') {
        return data.chargedAmount !== undefined && data.chargedAmount > 0;
      }
      return true;
    },
    {
      message: 'Charged amount is required when fines are imposed',
      path: ['chargedAmount'],
    }
  );

const facilitySchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  identifierNumber: z.string().min(1, 'Identifier number is required'),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(val => !val || val === '' || /^\+?[\d\s-()]+$/.test(val), 'Please enter a valid phone number'),
  nidNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(val => !val || val === '' || (val.length === 16 && /^\d{16}$/.test(val)), 'NID must be exactly 16 digits'),
  locationId: z.string().optional().or(z.literal('')),
  facilityTypeId: z.string().optional().or(z.literal('')),
  coordinates: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      val => !val || val === '' || /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(val),
      'Coordinates must be in format: lat,lng'
    ),
});

type AssessmentForm = z.infer<typeof assessmentSchema>;
type FacilityForm = z.infer<typeof facilitySchema>;

// Persistent state interface to maintain data across steps
interface AssessmentState {
  selectedFacility: string;
  assessmentType: AssessmentType | null;
  questionnaire: QuestionnaireDto | null;
  answers: AnswerDto[];
  complianceQuestions: ComplianceQuestionTreeDto[];
  /** Section metadata from GET current compliance questionnaire (for grouping in UI). */
  complianceSections: QuestionnaireSectionDto[];
  /** All question IDs in the merged tree (questionnaire + flat facility-type extras), for drafts and debugging. */
  complianceQuestionnaireIds: string[];
  complianceAnswers: ComplianceResponseItemDto[];
  /** Dynamically loaded sub-questions keyed by parent question ID. Kept in assessmentState so
   *  functional updaters always read the latest value (avoids stale closure bugs). */
  loadedSubQuestions: Record<string, ComplianceQuestionTreeDto[]>;
  requirements: any[];
  uploadedImages: UploadedImage[];
  currentStep: 'facility' | 'type' | 'general' | 'questions' | 'review';
}

// Draft state interface for localStorage — complianceQuestions stored as tree
interface AssessmentDraft extends AssessmentState {
  timestamp: number;
  userId: string;
  selectedFacilityTypeId: string | null;
  finalDecision?: string;
  decisions?: string[];
  chargedAmount?: number;
  recommendations?: RecommendationItemDto[];
  committeeMembers?: { name: string; phone: string; position: string }[];
  additionalComment?: string;
  followupDate?: string;
  assessmentCoordinates?: string;
  facilityCoordinates?: string;
}

// Draft Management Utilities
const DRAFT_STORAGE_KEY_PREFIX = 'nhip_assessment_draft_';
const DRAFT_MAX_AGE_DAYS = 7;

const getDraftStorageKey = (userId: string) => `${DRAFT_STORAGE_KEY_PREFIX}${userId}`;

const saveDraftToLocalStorage = (draft: AssessmentDraft) => {
  try {
    const key = getDraftStorageKey(draft.userId);
    localStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    console.error('Failed to save draft:', error);
  }
};

const getDraftFromLocalStorage = (userId: string): AssessmentDraft | null => {
  try {
    const key = getDraftStorageKey(userId);
    const draftStr = localStorage.getItem(key);
    if (!draftStr) return null;

    const draft: AssessmentDraft = JSON.parse(draftStr);

    // Check if draft belongs to current user
    if (draft.userId !== userId) {
      return null;
    }

    // Check if draft is too old
    const ageInDays = (Date.now() - draft.timestamp) / (1000 * 60 * 60 * 24);
    if (ageInDays > DRAFT_MAX_AGE_DAYS) {
      localStorage.removeItem(key);
      return null;
    }

    return draft;
  } catch (error) {
    console.error('Failed to load draft:', error);
    return null;
  }
};

const clearDraftFromLocalStorage = (userId: string) => {
  try {
    const key = getDraftStorageKey(userId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear draft:', error);
  }
};

// Helper function to get localized facility type name (moved outside component for optimization)
const getLocalizedFacilityTypeName = (facilityType: FacilityTypeDto, locale: string): string => {
  switch (locale) {
    case 'fr':
      return facilityType.nameFrench || facilityType.name;
    case 'rw':
      return facilityType.nameKinyarwanda || facilityType.name;
    case 'en':
    default:
      return facilityType.name;
  }
};

export default function NewAssessment() {
  const { dictionary, locale } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = getCurrentUser();

  // Use a single state object to maintain all assessment data
  const [assessmentState, setAssessmentState] = useState<AssessmentState>({
    selectedFacility: '',
    assessmentType: null,
    questionnaire: null,
    answers: [],
    complianceQuestions: [],
    complianceSections: [],
    complianceQuestionnaireIds: [],
    complianceAnswers: [],
    loadedSubQuestions: {},
    requirements: [],
    uploadedImages: [],
    currentStep: 'type',
  });

  // Destructure state for easier access
  const {
    selectedFacility,
    assessmentType,
    questionnaire,
    answers,
    complianceQuestions,
    complianceSections,
    complianceQuestionnaireIds,
    complianceAnswers,
    loadedSubQuestions,
    requirements,
    uploadedImages,
    currentStep,
  } = assessmentState;

  const [showCreateFacilityModal, setShowCreateFacilityModal] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  // Location filter for the facility picker — pre-filled for non-central users
  const [facilityFilterLocationId, setFacilityFilterLocationId] = useState<string | undefined>(undefined);
  const [facilityFilterResetKey, setFacilityFilterResetKey] = useState(0);
  const [facilityCoordinates, setFacilityCoordinates] = useState<string>(''); // GPS coordinates for facility during assessment
  const [selectedFacilityTypeId, setSelectedFacilityTypeId] = useState<string | null>(null);
  const [selectedFilterSubcategoryId, setSelectedFilterSubcategoryId] = useState<string>('');
  const [isGettingLocation, setIsGettingLocation] = useState(false); // For assessment coordinates, not facility creation
  const [isHomeFacility, setIsHomeFacility] = useState(false);
  const [facilityTypes, setFacilityTypes] = useState<FacilityTypeDto[]>([]);
  const [facilityTypesLoading, setFacilityTypesLoading] = useState(false);
  const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(false);

  const [questionnaireEmptyMessage, setQuestionnaireEmptyMessage] = useState<string | null>(null);


  const [checklistAvailable, setChecklistAvailable] = useState<boolean | null>(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [complianceReferenceDocuments, setComplianceReferenceDocuments] = useState<QuestionnaireDocumentDto[]>([]);
  const [showComplianceDocumentViewer, setShowComplianceDocumentViewer] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [savedDraft, setSavedDraft] = useState<AssessmentDraft | null>(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [isGettingAssessmentLocation, setIsGettingAssessmentLocation] = useState(false);
  const [committeeMembers, setCommitteeMembers] = useState<{ name: string; phone: string; position: string }[]>([]);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberPhone, setNewMemberPhone] = useState('');
  const [newMemberPosition, setNewMemberPosition] = useState('');

  /** Committee phone: digits only, max 10 (Rwanda-style local length cap). */
  const sanitizeCommitteePhone = (value: string) => value.replace(/\D/g, '').slice(0, 10);
  const [selectedDecisions, setSelectedDecisions] = useState<FinalDecision[]>(['NO_ACTION']);
  const [recommendations, setRecommendations] = useState<RecommendationItemDto[]>([]);
  const [isRestoringDraft, setIsRestoringDraft] = useState(false);
  const [reviewSubquestions, setReviewSubquestions] = useState<Record<string, any[]>>({});
  const [loadingReviewSubquestions, setLoadingReviewSubquestions] = useState<Record<string, boolean>>({});
  const [subquestionDialog, setSubquestionDialog] = useState<{
    isOpen: boolean;
    optionId: string;
    questionText: string;
  }>({
    isOpen: false,
    optionId: '',
    questionText: '',
  });

  // Form declarations moved to the top
  const {
    register: assessmentRegister,
    handleSubmit: handleAssessmentSubmit,
    formState: { errors: assessmentErrors },
    setValue: assessmentSetValue,
    watch,
    trigger,
  } = useForm<AssessmentForm>({
    resolver: zodResolver(assessmentSchema),
    defaultValues: {
      finalDecision: 'NO_ACTION',
      answers: [],
      requirements: [],
      photos: [],
      committee: [],
      coordinates: '',
      additionalComment: '',
      followupDate: '',
      isFlagged: false,
    },
  });

  const {
    register: facilityRegister,
    handleSubmit: handleFacilitySubmit,
    formState: { errors: facilityErrors, isSubmitted: facilityIsSubmitted },
    reset: facilityReset,
    setValue: facilitySetValue,
    watch: facilityWatch,
    clearErrors: facilityClearErrors,
    setError: facilitySetError,
  } = useForm<FacilityForm>({
    resolver: zodResolver(facilitySchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Watch all facility form fields for error clearing
  const watchedFacilityTitle = facilityWatch('title');
  const watchedFacilityIdentifier = facilityWatch('identifierNumber');
  const watchedFacilityContactNumber = facilityWatch('contactNumber');
  const watchedFacilityTypeId = facilityWatch('facilityTypeId');
  const watchedNidNumber = facilityWatch('nidNumber');

  // Watch assessment form fields for draft saving
  const photos = watch('photos') || [];
  const finalDecision = watch('finalDecision');
  const chargedAmount = watch('chargedAmount');
  const additionalComment = watch('additionalComment');
  const assessmentCoordinates = watch('coordinates');

  // Keep form finalDecision in sync with selectedDecisions[0] for schema/refine
  useEffect(() => {
    if (selectedDecisions.length > 0) {
      assessmentSetValue('finalDecision', selectedDecisions[0]);
    }
  }, [selectedDecisions, assessmentSetValue]);

  // Find the selected facility type object
  const selectedFacilityType = facilityTypes.find(ft => ft.id === watchedFacilityTypeId);
  // Check if it's a HOME facility by checking all language fields (case-insensitive)
  const isCreatingHomeFacility = selectedFacilityType
    ? selectedFacilityType.name.toUpperCase() === 'HOME' ||
      selectedFacilityType.nameFrench?.toUpperCase() === 'MAISON' ||
      selectedFacilityType.nameKinyarwanda?.toUpperCase() === 'URUGO'
    : false;

  // Sync identifier with NID when HOME facility type is selected
  useEffect(() => {
    if (isCreatingHomeFacility) {
      facilitySetValue('identifierNumber', watchedNidNumber || '');
    }
  }, [isCreatingHomeFacility, watchedNidNumber, facilitySetValue]);

  // Clear errors when user types in facility modal fields (only after first submit attempt)
  useEffect(() => {
    if (facilityIsSubmitted && watchedFacilityTitle && facilityErrors.title) {
      facilityClearErrors('title');
    }
  }, [watchedFacilityTitle, facilityIsSubmitted, facilityErrors.title, facilityClearErrors]);

  useEffect(() => {
    if (facilityIsSubmitted && watchedFacilityIdentifier && facilityErrors.identifierNumber) {
      facilityClearErrors('identifierNumber');
    }
  }, [watchedFacilityIdentifier, facilityIsSubmitted, facilityErrors.identifierNumber, facilityClearErrors]);

  useEffect(() => {
    if (facilityIsSubmitted && facilityErrors.contactNumber) {
      // Clear error if field becomes valid or empty
      if (
        !watchedFacilityContactNumber ||
        watchedFacilityContactNumber === '' ||
        /^\+?[\d\s-()]+$/.test(watchedFacilityContactNumber)
      ) {
        facilityClearErrors('contactNumber');
      }
    }
  }, [watchedFacilityContactNumber, facilityIsSubmitted, facilityErrors.contactNumber, facilityClearErrors]);

  useEffect(() => {
    if (facilityIsSubmitted && facilityErrors.nidNumber) {
      // Clear error if field becomes valid or empty
      const isValid =
        !watchedNidNumber ||
        watchedNidNumber === '' ||
        (watchedNidNumber.length === 16 && /^\d{16}$/.test(watchedNidNumber));
      if (isValid) {
        facilityClearErrors('nidNumber');
      }
    }
  }, [watchedNidNumber, facilityIsSubmitted, facilityErrors.nidNumber, facilityClearErrors]);

  // Use the new useAssessableFacilities hook instead of useFacilities
  const {
    data: assessableFacilitiesData,
    isLoading: facilitiesLoading,
    refetch: refetchFacilities,
  } = useAssessableFacilities(
    selectedFacilityTypeId
      ? {
          facilityTypeId: selectedFacilityTypeId,
          subcategoryId: selectedFilterSubcategoryId || undefined,
          assessmentType: assessmentType ?? undefined,
          locationId: facilityFilterLocationId,
        }
      : undefined
  );

  // Extract facilities from the assessable facilities response.
  // Memoized to prevent a new [] reference every render (which would trigger the compliance
  // loading effect on every render due to `facilities` being in its dependency array).
  const facilities = useMemo(() => assessableFacilitiesData?.facilities ?? [], [assessableFacilitiesData]);


  const createAssessmentMutation = useCreateAssessment();
  const createFacilityMutation = useCreateFacility();
  const { captureFromCamera, deleteImage, isUploading } = useCameraCapture();

  // Appeal resolutions: appealId → 'ACCEPTED' | 'REJECTED'
  const [appealResolutions, setAppealResolutions] = useState<Record<string, 'ACCEPTED' | 'REJECTED'>>({});

  const { data: locationHierarchy } = useQuery({
    queryKey: ['user-location-hierarchy'],
    queryFn: () => usersApi.getMyLocationHierarchy(),
    enabled: !!currentUser,
    staleTime: 10 * 60 * 1000,
  });

  // Pre-fill the location filter for non-central users once hierarchy is loaded
  useEffect(() => {
    if (!locationHierarchy) return;
    const isCentral = (currentUser as any)?.isCentralLevel;
    if (!isCentral && locationHierarchy.assignedLocationId && facilityFilterLocationId === undefined) {
      setFacilityFilterLocationId(locationHierarchy.assignedLocationId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHierarchy]);

  // Fetch previous assessment for the selected facility
  const { data: previousAssessment, isLoading: loadingPreviousAssessment } = useQuery({
    queryKey: ['previous-assessment', selectedFacility],
    queryFn: () => assessmentsApi.getPreviousAssessment(selectedFacility),
    enabled: !!selectedFacility,
  });

  const { data: hygieneAvailability, isLoading: hygieneAvailabilityLoading } = useQuery({
    queryKey: ['checklist-availability', selectedFacilityTypeId, 'HYGIENE'],
    queryFn: () => questionsApi.checkChecklistAvailability(selectedFacilityTypeId!, 'HYGIENE'),
    enabled: !!selectedFacilityTypeId && currentStep === 'type',
  });

  const { data: complianceAvailability, isLoading: complianceAvailabilityLoading } = useQuery({
    queryKey: ['checklist-availability', selectedFacilityTypeId, 'COMPLIANCE'],
    queryFn: () => questionsApi.checkChecklistAvailability(selectedFacilityTypeId!, 'COMPLIANCE'),
    enabled: !!selectedFacilityTypeId && currentStep === 'type',
  });

  // Pending recommendations from facility's latest assessment (for display in Previous Assessment section)
  const { data: pendingRecommendations } = useQuery({
    queryKey: ['pending-recommendations', selectedFacility, assessmentType],
    queryFn: () =>
      assessmentsApi.getPendingRecommendations(selectedFacility, {
        ...(assessmentType ? { assessmentType } : {}),
      }),
    enabled: !!selectedFacility,
  });

  const updateRecommendationResolvedMutation = useUpdateRecommendationResolved();

  // Sync facilityCoordinates state with form field. The submit payload sends
  // facilityCoordinates directly — this form field is only a mirror — so never
  // sync a malformed value into it where it would (invisibly) block validation.
  useEffect(() => {
    if (facilityCoordinates && COORDINATES_RE.test(facilityCoordinates)) {
      assessmentSetValue('coordinates', facilityCoordinates);
    }
  }, [facilityCoordinates, assessmentSetValue]);

  // Check which answers have subquestions when entering review step
  useEffect(() => {
    const checkForSubquestions = async () => {
      if (currentStep === 'review' && answers.length > 0) {
        const optionsToCheck = answers.map(a => a.selectedOptionId);

        for (const optionId of optionsToCheck) {
          // Only check if we haven't already fetched or checked this option
          if (!reviewSubquestions[optionId] && !loadingReviewSubquestions[optionId]) {
            try {
              const fetchedSubquestions = await questionnairesApi.getSubquestionsByOption(optionId);

              // Store even if empty so we don't check again
              if (fetchedSubquestions) {
                setReviewSubquestions(prev => ({ ...prev, [optionId]: fetchedSubquestions }));
              }
            } catch (error) {
              console.error('Error checking for subquestions:', error);
            }
          }
        }
      }
    };

    checkForSubquestions();
  }, [currentStep, answers]);

  // Memoize the fetchQuestionnaire function to prevent unnecessary re-renders
  const fetchQuestionnaire = useCallback(
    async (facilityId: string) => {
      setLoadingQuestionnaire(true);
      setQuestionnaireEmptyMessage(null);
      try {
        const response = await questionnairesApi.getByFacilityId(facilityId);
        setAssessmentState(prev => ({
          ...prev,
          questionnaire: response,
        }));
        assessmentSetValue('questionnaireId', response.id);
      } catch (error) {
        console.error('Error fetching questionnaire:', error);
        const backendMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '';
        if (backendMessage.includes('No HYGIENE questionnaire found')) {
          setQuestionnaireEmptyMessage(
            'No hygiene questionnaire is configured for this facility type. Please contact an administrator.'
          );
        } else {
          toast.error(dictionary['common.error']);
        }
        setAssessmentState(prev => ({
          ...prev,
          questionnaire: null,
        }));
      } finally {
        setLoadingQuestionnaire(false);
      }
    },
    [dictionary, assessmentSetValue]
  );

  useEffect(() => {
    if (showCreateFacilityModal && locationHierarchy?.assignedLocationId) {
      setSelectedLocationId(locationHierarchy.assignedLocationId);
    }
  }, [showCreateFacilityModal, locationHierarchy]);

  useEffect(() => {
    let isCancelled = false;

    const fetchFacilityTypes = async () => {
      setFacilityTypesLoading(true);
      try {
        const types = await facilityTypesApi.getAll();
        if (!isCancelled) setFacilityTypes(types);
      } catch (error) {
        console.error('Error fetching facility types:', error);
      } finally {
        if (!isCancelled) setFacilityTypesLoading(false);
      }
    };

    // Initial load
    void fetchFacilityTypes();

    // If a facility type was created while this page was open (e.g. opened in another tab
    // then you return), refresh when the tab becomes visible again.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchFacilityTypes();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isCancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Load draft on component mount
  useEffect(() => {
    if (!currentUser?.id || isDraftLoaded) return;

    const draft = getDraftFromLocalStorage(currentUser.id);
    if (draft) {
      setSavedDraft(draft);
      setShowDraftBanner(true);
    }
    setIsDraftLoaded(true);
  }, [currentUser?.id, isDraftLoaded]);

  // Auto-save draft whenever important state changes
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Don't save if we haven't loaded a potential draft yet
    if (!isDraftLoaded || !currentUser?.id) return;

    // Don't save if we're on facility step with nothing selected
    if (currentStep === 'facility' && !selectedFacility) return;

    // Don't save empty state
    if (!selectedFacility && answers.length === 0 && uploadedImages.length === 0) return;

    const draft: AssessmentDraft = {
      selectedFacility,
      assessmentType,
      questionnaire,
      answers,
      complianceQuestions,
      complianceSections,
      complianceQuestionnaireIds,
      complianceAnswers,
      // Persisted with the draft: sub-question defs are needed to keep sub-answers
      // visible/submittable after a restore (they are NOT re-fetched automatically).
      loadedSubQuestions,
      requirements,
      uploadedImages,
      currentStep,
      selectedFacilityTypeId,
      finalDecision: finalDecision,
      decisions: selectedDecisions.length > 0 ? selectedDecisions : undefined,
      // NaN would survive as null through JSON and poison the restored form
      chargedAmount: typeof chargedAmount === 'number' && Number.isFinite(chargedAmount) ? chargedAmount : undefined,
      recommendations: recommendations.length > 0 ? recommendations : undefined,
      committeeMembers: committeeMembers,
      additionalComment: additionalComment,
      assessmentCoordinates: assessmentCoordinates,
      facilityCoordinates: facilityCoordinates,
      timestamp: Date.now(),
      userId: currentUser.id,
    };

    // Debounce the save to avoid too many writes
    const timeoutId = setTimeout(() => {
      // Only save if component is still mounted
      if (isMountedRef.current) {
        saveDraftToLocalStorage(draft);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    selectedFacility,
    assessmentType,
    questionnaire,
    answers,
    complianceQuestions,
    complianceSections,
    complianceQuestionnaireIds,
    complianceAnswers,
    loadedSubQuestions,
    requirements,
    uploadedImages,
    currentStep,
    selectedFacilityTypeId,
    finalDecision,
    chargedAmount,
    selectedDecisions,
    recommendations,
    committeeMembers,
    additionalComment,
    assessmentCoordinates,
    facilityCoordinates,
    currentUser?.id,
    isDraftLoaded,
  ]);

  // Handle facilityId from URL parameters
  const hasInitializedFromUrlRef = React.useRef(false);

  useEffect(() => {
    // Only run once when component mounts and facilities are loaded
    if (hasInitializedFromUrlRef.current || !facilities.length) {
      return;
    }

    const facilityIdFromUrl = searchParams.get('facilityId');
    if (facilityIdFromUrl) {
      // Check if the facility exists in the facilities list
      const facilityExists = facilities.find(f => f.id === facilityIdFromUrl);
      if (facilityExists) {
        // Pre-select the facility and its type
        setAssessmentState(prev => ({
          ...prev,
          selectedFacility: facilityIdFromUrl,
        }));
        assessmentSetValue('facilityId', facilityIdFromUrl);

        // Also set the facility type for filtering
        if (facilityExists.facilityType?.id) {
          setSelectedFacilityTypeId(facilityExists.facilityType.id);
        }

        hasInitializedFromUrlRef.current = true;
      }
    }
  }, [facilities, searchParams, assessmentSetValue]);


  // Check checklist availability when facility type or assessment type context changes
  useEffect(() => {
    if (!selectedFacilityTypeId) {
      setChecklistAvailable(null);
      return;
    }
    const type = assessmentType === 'COMPLIANCE' ? 'COMPLIANCE' : 'HYGIENE';
    questionsApi
      .checkChecklistAvailability(selectedFacilityTypeId, type)
      .then(result => setChecklistAvailable(result.available))
      .catch(() => setChecklistAvailable(null));
  }, [selectedFacilityTypeId, assessmentType]);

  // Use ref to track previous facility to avoid dependency on facilities array
  const prevFacilityIdRef = React.useRef<string>('');
  const skipLoadAfterRestoreRef = React.useRef(false);

  // Fixed useEffect - only run when selectedFacility changes
  useEffect(() => {
    // Only run if the facility ID actually changed
    if (prevFacilityIdRef.current === selectedFacility) {
      return;
    }

    prevFacilityIdRef.current = selectedFacility;

    if (selectedFacility) {
      const facility = facilities.find(f => f.id === selectedFacility);
      if (facility) {
        const facilityTypeName = facility.facilityType?.name?.toLowerCase() || '';
        setIsHomeFacility(facilityTypeName.includes('home') || facilityTypeName.includes('house'));
        if (facility.coordinates) {
          setFacilityCoordinates(facility.coordinates);
        }
      }
    } else {
      setQuestionnaireEmptyMessage(null);
      setChecklistAvailable(null);
      setAssessmentState(prev => ({
        ...prev,
        assessmentType: null,
        questionnaire: null,
        answers: [],
        complianceQuestions: [],
        complianceSections: [],
        complianceQuestionnaireIds: [],
        complianceAnswers: [],
        loadedSubQuestions: {},
        uploadedImages: [],
      }));
      setIsHomeFacility(false);
      setFacilityCoordinates('');
      assessmentSetValue('photos', []);
      assessmentSetValue('answers', []);
      assessmentSetValue('requirements', []);
    }
  }, [selectedFacility, facilities, assessmentSetValue]);

  // Tracks the (assessmentType, facility) pair the questions were loaded for, so the effect
  // below only reloads on a REAL change — not when unrelated deps (e.g. the `facilities`
  // array identity after a refetch) churn. A spurious reload mid-assessment used to remount
  // the questionnaire form / clear compliance answers, deleting sub-question responses.
  const questionsLoadKeyRef = React.useRef<string>('');

  // Load questionnaire (HYGIENE) or compliance questions (COMPLIANCE) only after user chooses assessment type
  useEffect(() => {
    if (!selectedFacility || !assessmentType) {
      questionsLoadKeyRef.current = '';
      return;
    }
    const loadKey = `${assessmentType}|${selectedFacility}`;
    if (skipLoadAfterRestoreRef.current) {
      skipLoadAfterRestoreRef.current = false;
      questionsLoadKeyRef.current = loadKey; // restored from draft — treat as already loaded
      return;
    }
    if (questionsLoadKeyRef.current === loadKey) return; // already loaded for this facility+type
    setQuestionnaireEmptyMessage(null);
    const facility = facilities.find(f => f.id === selectedFacility);
    const facilityTypeId = facility?.facilityType?.id ?? selectedFacilityTypeId;
    if (assessmentType === 'HYGIENE') {
      questionsLoadKeyRef.current = loadKey;
      setAssessmentState(prev => ({
        ...prev,
        complianceQuestions: [],
        complianceSections: [],
        complianceQuestionnaireIds: [],
        complianceAnswers: [],
        loadedSubQuestions: {},
      }));
      setLoadingCompliance(false);
      fetchQuestionnaire(selectedFacility);
    } else if (assessmentType === 'COMPLIANCE' && facilityTypeId) {
      questionsLoadKeyRef.current = loadKey;
      setAssessmentState(prev => ({
        ...prev,
        questionnaire: null,
        answers: [],
        complianceSections: [],
        complianceQuestions: [],
        complianceQuestionnaireIds: [],
        complianceAnswers: [],
        loadedSubQuestions: {},
      }));
      assessmentSetValue('questionnaireId', '');
      assessmentSetValue('answers', []);
      setLoadingCompliance(true);
      Promise.all([
        complianceApi.getCurrentQuestionnaire(facilityTypeId, facility?.id),
        complianceApi.getQuestionsByFacilityType(facilityTypeId).catch(() => []),
      ])
        .then(([result, flat]) => {
          const questions = result.questions ?? [];
          const qid = result.questionnaire?.id ?? questions[0]?.questionnaireId ?? '';
          const mergedQuestions = mergeFlatComplianceQuestionsIntoTree(questions, flat, qid);
          const questionnaireIds = Array.from(collectComplianceQuestionTreeIds(mergedQuestions));
          setAssessmentState(prev => ({
            ...prev,
            complianceQuestions: mergedQuestions,
            complianceQuestionnaireIds: questionnaireIds,
            complianceSections: result.sections ?? [],
            complianceAnswers: [],
          }));
          if (mergedQuestions.length === 0) {
            setQuestionnaireEmptyMessage(
              'No compliance questionnaire is configured for this facility type. Please contact an administrator.'
            );
          }
        })
        .catch((error) => {
          const backendMessage =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message || '';
          if (backendMessage.includes('No COMPLIANCE questionnaire found')) {
            setQuestionnaireEmptyMessage(
              'No compliance questionnaire is configured for this facility type. Please contact an administrator.'
            );
          } else {
            toast.error(dictionary['common.error']);
          }
          setAssessmentState(prev => ({
            ...prev,
            complianceQuestions: [],
            complianceSections: [],
            complianceQuestionnaireIds: [],
            complianceAnswers: [],
          }));
        })
        .finally(() => setLoadingCompliance(false));
    }
  }, [assessmentType, selectedFacility, facilities, selectedFacilityTypeId, fetchQuestionnaire, assessmentSetValue]);

  // Reference PDFs (governing regulations/guidelines) for the compliance questionnaire(s) in play.
  useEffect(() => {
    if (assessmentType !== 'COMPLIANCE' || complianceQuestionnaireIds.length === 0) {
      setComplianceReferenceDocuments([]);
      return;
    }
    let cancelled = false;
    Promise.all(complianceQuestionnaireIds.map(id => questionnairesApi.getReferenceDocuments(id).catch(() => [])))
      .then(results => {
        if (cancelled) return;
        const merged = results.flat();
        const deduped = Array.from(new Map(merged.map(doc => [doc.id, doc])).values());
        setComplianceReferenceDocuments(deduped);
      })
      .catch(error => console.error('Error fetching compliance reference documents:', error));
    return () => { cancelled = true; };
  }, [assessmentType, complianceQuestionnaireIds]);

  // Sync form values with state - use refs to avoid infinite loops
  // Consolidated effect to prevent circular dependencies
  const prevAnswersRef = React.useRef<AnswerDto[]>([]);
  const prevRequirementsRef = React.useRef<any[]>([]);
  const prevUploadedImagesRef = React.useRef<UploadedImage[]>([]);
  const isSyncingRef = React.useRef(false);

  useEffect(() => {
    // Prevent re-entrant calls during sync
    if (isSyncingRef.current) return;

    isSyncingRef.current = true;

    try {
      let hasChanges = false;

      // Only update if answers actually changed
      if (JSON.stringify(prevAnswersRef.current) !== JSON.stringify(answers)) {
        assessmentSetValue('answers', answers, { shouldValidate: false, shouldDirty: false });
        prevAnswersRef.current = answers;
        hasChanges = true;
      }

      // Only update if requirements actually changed
      if (JSON.stringify(prevRequirementsRef.current) !== JSON.stringify(requirements)) {
        assessmentSetValue('requirements', requirements, { shouldValidate: false, shouldDirty: false });
        prevRequirementsRef.current = requirements;
        hasChanges = true;
      }

      // Only update if uploadedImages actually changed
      if (JSON.stringify(prevUploadedImagesRef.current) !== JSON.stringify(uploadedImages)) {
        const photoIds = uploadedImages.map(img => img.id);
        assessmentSetValue('photos', photoIds, { shouldValidate: false, shouldDirty: false });
        prevUploadedImagesRef.current = uploadedImages;
        hasChanges = true;
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [answers, requirements, uploadedImages, assessmentSetValue]);

  // Sync committee members with form
  useEffect(() => {
    const memberNames = committeeMembers.map(m => `${m.name} (${m.position}) - ${m.phone}`);
    assessmentSetValue('committee', memberNames);
  }, [committeeMembers, assessmentSetValue]);

  const userCanCreateAssessment = canCreateAssessment(currentUser?.role as UserRole);

  const handleCaptureImage = async () => {
    try {
      const result = await captureFromCamera();

      if (result) {
        const newImage: UploadedImage = {
          id: result.id,
          url: result.url,
          filename: result.name || result.originalName || 'captured-image.jpg',
        };
        setAssessmentState(prev => ({
          ...prev,
          uploadedImages: [...prev.uploadedImages, newImage],
        }));
        // Success toast is already shown by useCameraCapture hook
      }
    } catch (error) {
      console.error('Unexpected error during camera capture:', error);
      const errorMessage = error instanceof Error ? error.message : dictionary['new_assessment.failed_capture_image'];
      toast.error(errorMessage);
    }
  };

  const handleDeleteImage = async (image: UploadedImage) => {
    try {
      await deleteImage(image.id);
      setAssessmentState(prev => ({
        ...prev,
        uploadedImages: prev.uploadedImages.filter(img => img.id !== image.id),
      }));
      toast.success(dictionary['new_assessment.image_deleted_success']);
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error(dictionary['new_assessment.failed_delete_image']);
    }
  };

  const getRequirement = (type: RequirementType) => {
    return requirements.find(r => r.requirementType === type);
  };

  const updateRequirement = (type: RequirementType, updates: any) => {
    const currentRequirements = [...requirements];
    const existingIndex = currentRequirements.findIndex(r => r.requirementType === type);

    if (existingIndex >= 0) {
      currentRequirements[existingIndex] = {
        ...currentRequirements[existingIndex],
        ...updates,
      };
    } else {
      currentRequirements.push({
        requirementType: type,
        isAvailable: true,
        ...updates,
      });
    }
    setAssessmentState(prev => ({
      ...prev,
      requirements: currentRequirements,
    }));
  };

  const isGeneralQuestionsValid = () => {
    if (isHomeFacility) {
      const toiletReq = getRequirement(RequirementType.STANDARD_TOILET);
      const houseReq = getRequirement(RequirementType.STANDARD_HOUSE);

      if (!toiletReq || toiletReq.isAvailable === undefined) return false;
      if (!houseReq || houseReq.isAvailable === undefined) return false;

      if (toiletReq.isAvailable === false) {
        if (toiletReq.hasLand === undefined || toiletReq.hasFunds === undefined) return false;
      }
      if (houseReq.isAvailable === false) {
        if (houseReq.hasLand === undefined || houseReq.hasFunds === undefined) return false;
      }

      return true;
    } else {
      const handwashingReq = getRequirement(RequirementType.HANDWASHING_FACILITY);
      const waterReq = getRequirement(RequirementType.CLEAN_WATER_FACILITY);
      const wasteReq = getRequirement(RequirementType.WASTE_MANAGEMENT_FACILITY);

      if (!handwashingReq || handwashingReq.isAvailable === undefined) return false;
      if (!waterReq || waterReq.isAvailable === undefined) return false;
      if (!wasteReq || wasteReq.isAvailable === undefined) return false;

      if (handwashingReq.isAvailable === false) {
        if (handwashingReq.hasLand === undefined || handwashingReq.hasFunds === undefined) return false;
      }
      if (waterReq.isAvailable === false) {
        if (waterReq.hasLand === undefined || waterReq.hasFunds === undefined) return false;
      }
      if (wasteReq.isAvailable === false) {
        if (wasteReq.hasLand === undefined || wasteReq.hasFunds === undefined) return false;
      }

      return true;
    }
  };

  // Validate that all main questions are answered
  // Note: We check main questions only because subquestions are conditional
  // and may not always be present. The QuestionnaireForm component handles
  // the proper validation of all visible questions including subquestions.
  const areAllQuestionsAnswered = () => {
    if (!questionnaire?.questions) return false;

    const selectedOptionIds = new Set(answers.map(a => a.selectedOptionId));
    const mainQuestions = questionnaire.questions.filter(
      q => !q.skipIfOptionId || !selectedOptionIds.has(q.skipIfOptionId)
    );
    const mainQuestionIds = mainQuestions.map(q => q.id);
    const answeredMainQuestions = answers.filter(a => mainQuestionIds.includes(a.questionId));

    // Check if all main questions have been answered
    return answeredMainQuestions.length === mainQuestions.length && mainQuestions.length > 0;
  };

  // Surface schema-validation failures. Without this, a rejected field (even an
  // invisible one like `coordinates`) made the Submit button silently do nothing.
  const onAssessmentInvalid = (formErrors: FieldErrors<AssessmentForm>) => {
    const firstError = Object.values(formErrors).find(
      (e): e is { message: string } => Boolean(e && typeof (e as { message?: unknown }).message === 'string')
    );
    toast.error(
      firstError?.message ||
        dictionary['new_assessment.error_validation'] ||
        'Some fields are invalid. Please review the form and try again.'
    );
  };

  const onAssessmentSubmit = async (data: AssessmentForm) => {
    try {
      if (!selectedFacility) {
        toast.error(dictionary['new_assessment.error_no_facility'] || 'Please select a facility');
        return;
      }
      if (!assessmentType) {
        toast.error(dictionary['new_assessment.error_no_type'] || 'Please choose an assessment type');
        return;
      }
      if (requirements.length === 0) {
        toast.error(
          dictionary['new_assessment.error_no_requirements'] || 'Please complete the general requirements section'
        );
        return;
      }
      if (selectedDecisions.length === 0) {
        toast.error(dictionary['new_assessment.error_no_decision'] || 'Please select at least one decision');
        return;
      }
      if (committeeMembers.length < 2) {
        toast.error(dictionary['new_assessment.error_committee_min'] || 'At least 2 committee members are required');
        return;
      }
      if (selectedDecisions.includes('FINES') && (!data.chargedAmount || data.chargedAmount <= 0)) {
        toast.error(dictionary['new_assessment.error_no_fine_amount'] || 'Please enter a fine amount');
        return;
      }
      const recommendationsToSend = recommendations
        .filter(r => r.recommendation?.trim())
        .map(r => ({ recommendation: r.recommendation.trim(), deadline: r.deadline || undefined }));

      const resolvedAppeals: AppealResolutionDto[] = Object.entries(appealResolutions).map(
        ([appealId, resolution]) => ({ appealId, resolution })
      );

      const basePayload = {
        assessmentType,
        facilityId: selectedFacility,
        coordinates: facilityCoordinates.trim(),
        decisions: selectedDecisions,
        finalDecision: selectedDecisions[0],
        photos: data.photos ?? [],
        requirements: data.requirements ?? requirements,
        committee: data.committee,
        additionalComment: data.additionalComment,
        chargedAmount: data.chargedAmount,
        ...(recommendationsToSend.length > 0 && { recommendations: recommendationsToSend }),
        ...(data.isFlagged === true && { isFlagged: true }),
        ...(resolvedAppeals.length > 0 && { appealResolutions: resolvedAppeals }),
      };

      let assessmentData: CreateAssessmentDto;

      if (assessmentType === 'HYGIENE') {
        if (!questionnaire?.id) {
          toast.error(dictionary['new_assessment.error_no_questionnaire'] || 'No questionnaire loaded');
          return;
        }
        if (answers.length === 0) {
          toast.error(dictionary['new_assessment.error_incomplete_answers'] || 'Please answer the assessment questions');
          return;
        }
        assessmentData = {
          ...basePayload,
          questionnaireId: questionnaire.id,
          answers,
        };
      } else {
        if (complianceQuestions.length === 0) {
          toast.error(dictionary['new_assessment.error_no_compliance_questions'] || 'No compliance questions loaded');
          return;
        }
        if (!allVisibleComplianceAnswered(complianceQuestions, complianceAnswers, loadedSubQuestions)) {
          toast.error(
            dictionary['new_assessment.error_compliance_incomplete'] || 'Please answer all compliance questions'
          );
          return;
        }
        // Send every answer that is either provably visible OR unknown to the local
        // question graph. Unknown answers (e.g. sub-answers whose defs weren't reloaded
        // after a draft restore) must be KEPT — the backend validates and prunes stale
        // IDs itself. Only answers we can PROVE are hidden (skip logic / deselected
        // branch) are excluded. Intersecting with "known" IDs here is what used to
        // silently delete sub-question responses.
        const visibleIds = getVisibleComplianceQuestionIds(
          complianceQuestions,
          complianceAnswers,
          loadedSubQuestions
        );
        const knownIds = allowedComplianceResponseQuestionIds(complianceQuestions, loadedSubQuestions);
        const complianceResponses = complianceAnswers.filter(
          a => visibleIds.has(a.questionId) || !knownIds.has(a.questionId)
        );
        assessmentData = {
          ...basePayload,
          complianceResponses,
        };
      }

      try {
        await createAssessmentMutation.mutateAsync(assessmentData);
      } catch (submitError) {
        const backendMessage =
          (submitError as { response?: { data?: { message?: string } } })?.response?.data?.message || '';
        const invalidComplianceIdMatch = backendMessage.match(
          /Compliance question\s+([0-9a-fA-F-]{8,})\s+not found or does not apply/i
        );

        if (assessmentType === 'COMPLIANCE' && invalidComplianceIdMatch) {
          const invalidQuestionId = invalidComplianceIdMatch[1];
          const currentResponses = (assessmentData as CreateAssessmentDto).complianceResponses ?? [];
          const prunedResponses = currentResponses.filter(r => r.questionId !== invalidQuestionId);

          if (prunedResponses.length < currentResponses.length) {
            const retryPayload: CreateAssessmentDto = {
              ...(assessmentData as CreateAssessmentDto),
              complianceResponses: prunedResponses,
            };
            await createAssessmentMutation.mutateAsync(retryPayload);
          } else {
            throw submitError;
          }
        } else {
          throw submitError;
        }
      }
      // Clear draft after successful submission
      if (currentUser?.id) {
        clearDraftFromLocalStorage(currentUser.id);
      }
      // Success toast is shown by useCreateAssessment hook
      navigate('/assessments');
    } catch (error) {
      console.error('Assessment submission failed:', error);
      // Error toast is shown by handleApiError in useCreateAssessment hook
    }
  };

  const handleRestoreDraft = async () => {
    if (!savedDraft) return;

    setIsRestoringDraft(true);
    skipLoadAfterRestoreRef.current = true;

    try {
      // Restore dynamically-loaded sub-question defs saved with the draft. Without them,
      // restored sub-answers could not be classified and were silently dropped at submit.
      const restoredLoadedSubQuestions: Record<string, ComplianceQuestionTreeDto[]> = {};
      Object.entries(savedDraft.loadedSubQuestions ?? {}).forEach(([parentId, subs]) => {
        restoredLoadedSubQuestions[parentId] = normalizeComplianceQuestionTree(subs ?? []);
      });

      // Restore state
      setAssessmentState({
        selectedFacility: savedDraft.selectedFacility,
        assessmentType: savedDraft.assessmentType ?? null,
        questionnaire: savedDraft.questionnaire,
        answers: savedDraft.answers,
        complianceQuestions: normalizeComplianceQuestionTree(savedDraft.complianceQuestions ?? []),
        complianceSections: normalizeQuestionnaireSections(savedDraft.complianceSections ?? []),
        complianceQuestionnaireIds: savedDraft.complianceQuestionnaireIds ?? [],
        complianceAnswers: savedDraft.complianceAnswers ?? [],
        loadedSubQuestions: restoredLoadedSubQuestions,
        requirements: savedDraft.requirements,
        uploadedImages: savedDraft.uploadedImages,
        currentStep: savedDraft.currentStep,
      });

      // Restore facility type
      if (savedDraft.selectedFacilityTypeId) {
        setSelectedFacilityTypeId(savedDraft.selectedFacilityTypeId);
      }

      // Restore all form values
      if (savedDraft.selectedFacility) {
        assessmentSetValue('facilityId', savedDraft.selectedFacility);
      }
      if (savedDraft.questionnaire?.id) {
        assessmentSetValue('questionnaireId', savedDraft.questionnaire.id);
      }
      if (savedDraft.answers && savedDraft.answers.length > 0) {
        assessmentSetValue('answers', savedDraft.answers);
      }
      if (savedDraft.requirements && savedDraft.requirements.length > 0) {
        assessmentSetValue('requirements', savedDraft.requirements);
      }
      if (savedDraft.uploadedImages && savedDraft.uploadedImages.length > 0) {
        const photoIds = savedDraft.uploadedImages.map(img => img.id);
        assessmentSetValue('photos', photoIds);
      }
      if (savedDraft.finalDecision) {
        assessmentSetValue('finalDecision', savedDraft.finalDecision as any);
      }
      if (savedDraft.decisions && savedDraft.decisions.length > 0) {
        setSelectedDecisions(savedDraft.decisions as FinalDecision[]);
      } else if (savedDraft.finalDecision) {
        setSelectedDecisions([savedDraft.finalDecision as FinalDecision]);
      }
      if (typeof savedDraft.chargedAmount === 'number' && Number.isFinite(savedDraft.chargedAmount)) {
        assessmentSetValue('chargedAmount', savedDraft.chargedAmount);
      }
      if (savedDraft.recommendations && savedDraft.recommendations.length > 0) {
        setRecommendations(savedDraft.recommendations);
      }
      if (savedDraft.committeeMembers && savedDraft.committeeMembers.length > 0) {
        const normalizedMembers = savedDraft.committeeMembers.map(m => ({
          ...m,
          phone: sanitizeCommitteePhone(m.phone || ''),
        }));
        setCommitteeMembers(normalizedMembers);
        const memberNames = normalizedMembers.map(m => `${m.name} (${m.position}) - ${m.phone}`);
        assessmentSetValue('committee', memberNames);
      }
      if (savedDraft.additionalComment) {
        assessmentSetValue('additionalComment', savedDraft.additionalComment);
      }
      if (savedDraft.followupDate) {
        assessmentSetValue('followupDate', savedDraft.followupDate);
      }
      if (savedDraft.assessmentCoordinates && COORDINATES_RE.test(savedDraft.assessmentCoordinates)) {
        assessmentSetValue('coordinates', savedDraft.assessmentCoordinates);
      }
      if (savedDraft.facilityCoordinates) {
        setFacilityCoordinates(savedDraft.facilityCoordinates);
      }

      // Refetch questionnaire to ensure it's up-to-date
      if (savedDraft.selectedFacility) {
        await fetchQuestionnaire(savedDraft.selectedFacility);
      }

      setShowDraftBanner(false);
      toast.success(dictionary['new_assessment.draft_restored']);
    } catch (error) {
      console.error('Error restoring draft:', error);
      toast.error(dictionary['new_assessment.draft_restore_error'] || 'Failed to restore draft');
    } finally {
      setIsRestoringDraft(false);
    }
  };

  const handleDiscardDraft = () => {
    if (currentUser?.id) {
      clearDraftFromLocalStorage(currentUser.id);
    }
    setSavedDraft(null);
    setShowDraftBanner(false);
    toast.success(dictionary['new_assessment.draft_discarded']);
  };

  const handleOpenSubquestionDialog = async (optionId: string, questionText: string) => {
    setSubquestionDialog({ isOpen: true, optionId, questionText });

    // If we already have subquestions for this option, don't fetch again
    if (reviewSubquestions[optionId] && reviewSubquestions[optionId].length > 0) {
      return;
    }

    // Fetch subquestions
    setLoadingReviewSubquestions(prev => ({ ...prev, [optionId]: true }));
    try {
      const fetchedSubquestions = await questionnairesApi.getSubquestionsByOption(optionId);

      if (fetchedSubquestions && fetchedSubquestions.length > 0) {
        setReviewSubquestions(prev => ({ ...prev, [optionId]: fetchedSubquestions }));
      }
    } catch (error) {
      console.error('Error fetching subquestions:', error);
    } finally {
      setLoadingReviewSubquestions(prev => ({ ...prev, [optionId]: false }));
    }
  };

  const onFacilitySubmit = async (data: FacilityForm) => {
    // Additional validation for HOME facility type - NID is required
    if (isCreatingHomeFacility) {
      if (!data.nidNumber || data.nidNumber.trim() === '') {
        facilitySetError('nidNumber', {
          type: 'manual',
          message: 'NID is required for home facilities',
        });
        toast.error(
          dictionary['facilities.nid_required_home'] || 'NID is required for home facilities and must be 16 digits'
        );
        return;
      } else if (data.nidNumber.length !== 16) {
        facilitySetError('nidNumber', {
          type: 'manual',
          message: 'NID must be exactly 16 digits',
        });
        toast.error(
          dictionary['facilities.nid_required_home'] || 'NID is required for home facilities and must be 16 digits'
        );
        return;
      }
    }

    // If HOME facility, use NID as identifier
    const identifierValue = isCreatingHomeFacility ? data.nidNumber || '' : data.identifierNumber;

    const facilityData: CreateFacilityDto = {
      title: data.title,
      // Coordinates are captured during assessment, not during facility creation
      identifierNumber: identifierValue,
      contactNumber: data.contactNumber && data.contactNumber.trim() !== '' ? data.contactNumber : undefined,
      nidNumber: data.nidNumber && data.nidNumber.trim() !== '' ? data.nidNumber : undefined,
      locationId: selectedLocationId || undefined,
      facilityTypeId: selectedFacilityTypeId || undefined,
    };

    try {
      const newFacility = await createFacilityMutation.mutateAsync(facilityData as CreateFacilityDto);

      // Store the facility type before resetting
      const currentFacilityTypeId = selectedFacilityTypeId;

      facilityReset();
      setShowCreateFacilityModal(false);
      setSelectedLocationId('');

      // Set state immediately so the user can proceed regardless of refetch timing
      setAssessmentState(prev => ({
        ...prev,
        selectedFacility: newFacility.id,
      }));
      assessmentSetValue('facilityId', newFacility.id);
      setSelectedFacilityTypeId(currentFacilityTypeId);

      // Set isHomeFacility directly from the returned facility data to avoid
      // the prevFacilityIdRef race condition with the async refetch
      if (newFacility.facilityType?.name) {
        const typeName = newFacility.facilityType.name.toLowerCase();
        setIsHomeFacility(
          typeName.includes('home') ||
          typeName.includes('house') ||
          newFacility.facilityType.nameKinyarwanda?.toUpperCase() === 'URUGO' ||
          newFacility.facilityType.nameFrench?.toUpperCase() === 'MAISON'
        );
      }

      // Refetch in background to update the dropdown — don't await so a
      // network hiccup can't block the user from proceeding
      refetchFacilities().catch(() => {});

      // Success toast is shown by useCreateFacility hook
    } catch (error) {
      console.error('Facility creation failed:', error);
      // Error toast is shown by handleApiError in useCreateFacility hook
    }
  };

  const getAssessmentLocation = () => {
    setIsGettingAssessmentLocation(true);

    if (!navigator.geolocation) {
      toast.error(dictionary['facilities.geolocation_not_supported'] || 'Geolocation not supported');
      setIsGettingAssessmentLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        const coordinates = `${latitude},${longitude}`;
        assessmentSetValue('coordinates', coordinates);
        toast.success(dictionary['facilities.location_success'] || 'Location obtained successfully');
        setIsGettingAssessmentLocation(false);
      },
      error => {
        console.error('Error getting location:', error);
        toast.error(dictionary['facilities.location_error'] || 'Failed to get location');
        setIsGettingAssessmentLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  };

  const handleFacilityTypeChange = (facilityTypeId: string | null) => {
    setSelectedFacilityTypeId(facilityTypeId);
    setSelectedFilterSubcategoryId('');
    setAssessmentState(prev => ({
      ...prev,
      selectedFacility: '',
    }));
    assessmentSetValue('facilityId', '');
  };

  const handleFacilityChange = (facilityId: string) => {
    setAssessmentState(prev => ({
      ...prev,
      selectedFacility: facilityId,
    }));
    assessmentSetValue('facilityId', facilityId);
  };

  const handleAnswersChange = (newAnswers: AnswerDto[]) => {
    setAssessmentState(prev => ({
      ...prev,
      answers: newAnswers,
    }));
  };

  const buildSelectedOptionIds = (items: ComplianceResponseItemDto[]) => {
    return new Set(
      items
        .map(item => {
          const raw = item as unknown as Record<string, unknown>;
          const id = item.selectedOptionId ?? raw.selected_option_id;
          return id != null && id !== '' ? String(id) : null;
        })
        .filter((id): id is string => Boolean(id))
    );
  };

  const findQuestionInComplianceTree = (
    nodes: ComplianceQuestionTreeDto[],
    id: string
  ): ComplianceQuestionTreeDto | null => {
    for (const n of nodes) {
      if (n.id === id) return n;
      const found = findQuestionInComplianceTree(n.subQuestions ?? [], id);
      if (found) return found;
    }
    return null;
  };

  /** Union tree + API-loaded sub-questions so visibility and clearing match rendering. */
  const mergeComplianceSubQuestionsById = (
    a: ComplianceQuestionTreeDto[],
    b: ComplianceQuestionTreeDto[]
  ): ComplianceQuestionTreeDto[] => {
    const map = new Map<string, ComplianceQuestionTreeDto>();
    for (const x of a) map.set(x.id, x);
    for (const x of b) map.set(x.id, x);
    return [...map.values()];
  };

  const getVisibleComplianceQuestionIds = (
    qs: ComplianceQuestionTreeDto[],
    answers: ComplianceResponseItemDto[],
    loadedSubsMap: Record<string, ComplianceQuestionTreeDto[]>
  ): Set<string> => {
    const visibleIds = new Set<string>();
    const answerByQuestion = new Map(answers.map(a => [a.questionId, a.selectedOptionId]));
    const selectedOptionIds = buildSelectedOptionIds(answers);

    const visit = (nodes: ComplianceQuestionTreeDto[]) => {
      for (const q of nodes) {
        if (!isComplianceQuestionTreeVisible(q, selectedOptionIds)) continue;

        visibleIds.add(q.id);
        const selectedOptionId = answerByQuestion.get(q.id);
        if (!selectedOptionId) continue;

        const mergedSubs = mergeComplianceSubQuestionsById(q.subQuestions ?? [], loadedSubsMap[q.id] ?? []);
        if (!mergedSubs.length) continue;

        const visibleSubs = mergedSubs.filter(sub => {
          const triggerMatches =
            !sub.triggerOptionId ||
            String(sub.triggerOptionId) === String(selectedOptionId ?? '');
          return triggerMatches && isComplianceQuestionTreeVisible(sub, selectedOptionIds);
        });
        visit(visibleSubs);
      }
    };

    visit(qs);
    return visibleIds;
  };

  const filterComplianceAnswersByVisibility = (
    qs: ComplianceQuestionTreeDto[],
    answers: ComplianceResponseItemDto[],
    loadedSubsMap: Record<string, ComplianceQuestionTreeDto[]>
  ): ComplianceResponseItemDto[] => {
    const visibleIds = getVisibleComplianceQuestionIds(qs, answers, loadedSubsMap);
    return answers.filter(a => visibleIds.has(a.questionId));
  };

  /** Top-level compliance roots grouped by questionnaire sections (matches integration guide). */
  const complianceRootsBySection = useMemo(() => {
    const roots = complianceQuestions;
    const orderedSections = [...complianceSections].sort(compareSectionOrder);
    const knownSectionIds = new Set(orderedSections.map(s => s.id));
    const groupedBySection: Record<string, ComplianceQuestionTreeDto[]> = {};
    const unsectionedRoots: ComplianceQuestionTreeDto[] = [];

    for (const q of roots) {
      const sid = q.sectionId;
      if (sid && knownSectionIds.has(sid)) {
        if (!groupedBySection[sid]) groupedBySection[sid] = [];
        groupedBySection[sid].push(q);
      } else {
        unsectionedRoots.push(q);
      }
    }
    for (const id of Object.keys(groupedBySection)) {
      groupedBySection[id].sort(compareComplianceQuestionOrder);
    }
    unsectionedRoots.sort(compareComplianceQuestionOrder);
    return { orderedSections, groupedBySection, unsectionedRoots };
  }, [complianceQuestions, complianceSections]);

  const handleComplianceAnswerChange = (questionId: string, selectedOptionId: string) => {
    // Guard: if the same option is already selected, bail out entirely.
    // Checked here (render-time value) to also skip the network fetch below.
    // The inner check inside setAssessmentState is a safety net for rapid sequential clicks.
    if (complianceAnswers.find(a => a.questionId === questionId)?.selectedOptionId === selectedOptionId) return;

    setAssessmentState(prev => {
      // Safety-net re-check with latest committed state (in case of rapid clicks before re-render)
      const currentAnswer = prev.complianceAnswers.find(a => a.questionId === questionId);
      if (currentAnswer?.selectedOptionId === selectedOptionId) return prev;

      // Remove old answer for this question
      const withoutCurrent = prev.complianceAnswers.filter(a => a.questionId !== questionId);

      // Recursively clear answers for all previously loaded sub-questions of this question.
      // Uses prev.loadedSubQuestions (not outer closure) to get the latest sub-question structure.
      const clearSubAnswers = (
        subQs: ComplianceQuestionTreeDto[],
        answers: ComplianceResponseItemDto[],
        subsMap: Record<string, ComplianceQuestionTreeDto[]>
      ): ComplianceResponseItemDto[] => {
        let result = answers;
        for (const sub of subQs) {
          result = result.filter(a => a.questionId !== sub.id);
          const nestedSubs = mergeComplianceSubQuestionsById(sub.subQuestions ?? [], subsMap[sub.id] ?? []);
          if (nestedSubs.length) result = clearSubAnswers(nestedSubs, result, subsMap);
        }
        return result;
      };

      const treeNode = findQuestionInComplianceTree(prev.complianceQuestions, questionId);
      const oldSubQs = mergeComplianceSubQuestionsById(
        treeNode?.subQuestions ?? [],
        prev.loadedSubQuestions[questionId] ?? []  // use prev — not outer closure
      );
      const clearedAnswers = oldSubQs.length
        ? clearSubAnswers(oldSubQs, withoutCurrent, prev.loadedSubQuestions)  // use prev — not outer closure
        : withoutCurrent;
      const nextAnswers = [...clearedAnswers, { questionId, selectedOptionId }];

      return {
        ...prev,
        // Keep all compliance answers in state (including hidden-after-skip)
        // so submit can include complete answer history expected by backend validation.
        complianceAnswers: nextAnswers,
      };
    });

    // Fetch sub-questions triggered by this option — merge result into assessmentState
    // so future functional updaters always see the latest loadedSubQuestions.
    complianceApi.getSubQuestionsForOption(questionId, selectedOptionId)
      .then(subQs => setAssessmentState(prev => ({
        ...prev,
        loadedSubQuestions: { ...prev.loadedSubQuestions, [questionId]: subQs },
      })))
      .catch(() => setAssessmentState(prev => ({
        ...prev,
        // On fetch failure keep whatever we already knew — overwriting with [] would
        // orphan already-given sub-answers.
        loadedSubQuestions: {
          ...prev.loadedSubQuestions,
          [questionId]: prev.loadedSubQuestions[questionId] ?? [],
        },
      })));
  };

  // Renders a compliance question and its visible sub-questions recursively
  const renderComplianceQuestion = (q: ComplianceQuestionTreeDto, index: number, depth: number): React.ReactNode => {
    const options = q.options || [];
    const selected = complianceAnswers.find(a => a.questionId === q.id)?.selectedOptionId;
    const selectedOptionIds = buildSelectedOptionIds(complianceAnswers);
    if (!isComplianceQuestionTreeVisible(q, selectedOptionIds)) return null;
    const questionText =
      locale === 'rw'
        ? q.questionKinyarwanda || q.question
        : locale === 'fr'
          ? q.questionFrench || q.question
          : q.question;

    const candidateSubs = mergeComplianceSubQuestionsById(q.subQuestions ?? [], loadedSubQuestions[q.id] ?? []);
    const visibleSubQuestions = candidateSubs.filter(sub => {
      const triggerMatches =
        !sub.triggerOptionId ||
        String(sub.triggerOptionId) === String(selected ?? '');
      return triggerMatches && isComplianceQuestionTreeVisible(sub, selectedOptionIds);
    });

    return (
      <div key={q.id} className={depth > 0 ? 'ml-4 border-l-2 border-primary/30 pl-4' : ''}>
        <div className='border border-gray-200 rounded-lg p-4'>
          <div className='mb-4'>
            <p className='text-xs font-semibold text-primary mb-1 uppercase tracking-wide'>
              {depth === 0
                ? `${dictionary['questionnaire.question']} ${index + 1}`
                : <span>↳ Follow-up</span>
              }
            </p>
            <p className='text-gray-800 font-medium'>{questionText}</p>
          </div>
          <div className='space-y-2'>
            {options.map(opt => {
              const optionText =
                locale === 'rw'
                  ? opt.optionKinyarwanda || opt.option
                  : locale === 'fr'
                    ? opt.optionFrench || opt.option
                    : opt.option;
              return (
                <label
                  key={opt.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    String(selected ?? '') === String(opt.id)
                      ? 'border-primary bg-secondary'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type='radio'
                    name={`compliance-${q.id}`}
                    checked={String(selected ?? '') === String(opt.id)}
                    onChange={() => handleComplianceAnswerChange(q.id, opt.id)}
                    className='mr-3 h-5 w-5 flex-shrink-0 border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2'
                    aria-label={optionText}
                  />
                  <div className='flex-1 min-w-0'>
                    <span className='text-gray-900'>{optionText}</span>
                  </div>
                  <span className='text-sm text-gray-500 ml-2 flex-shrink-0'>
                    ({opt.marks}/{q.weight} pts)
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        {visibleSubQuestions.map((sub, si) => renderComplianceQuestion(sub, si, depth + 1))}
      </div>
    );
  };

  const renderComplianceReviewCard = (q: ComplianceQuestionTreeDto, depth = 0): React.ReactNode => {
    const answer = complianceAnswers.find(a => a.questionId === q.id);
    const selectedOption = answer
      ? q.options?.find(opt => String(opt.id) === String(answer.selectedOptionId))
      : null;
    const questionText =
      locale === 'rw'
        ? q.questionKinyarwanda || q.question
        : locale === 'fr'
          ? q.questionFrench || q.question
          : q.question;
    const optionText = selectedOption
      ? locale === 'rw'
        ? selectedOption.optionKinyarwanda || selectedOption.option
        : locale === 'fr'
          ? selectedOption.optionFrench || selectedOption.option
          : selectedOption.option
      : null;

    // Show answered follow-up (sub) questions nested under their parent, using the same
    // trigger/visibility rules as the questions step.
    const selectedOptionIds = buildSelectedOptionIds(complianceAnswers);
    const mergedSubs = mergeComplianceSubQuestionsById(q.subQuestions ?? [], loadedSubQuestions[q.id] ?? []);
    const visibleSubQuestions = answer
      ? mergedSubs.filter(sub => {
          const triggerMatches =
            !sub.triggerOptionId || String(sub.triggerOptionId) === String(answer.selectedOptionId ?? '');
          return triggerMatches && isComplianceQuestionTreeVisible(sub, selectedOptionIds);
        })
      : [];

    return (
      <div key={q.id} className={depth > 0 ? 'border border-primary/30 bg-secondary/30 rounded p-2' : 'border border-gray-200 rounded p-2'}>
        <p className='text-xs font-medium text-gray-900 mb-1'>
          {depth > 0 && <span className='text-primary mr-1'>↳</span>}
          {questionText}
        </p>
        <span className='text-xs text-gray-600'>
          {optionText ?? (
            <span className='italic text-gray-400'>
              {dictionary['new_assessment.compliance_not_answered'] || 'Not answered'}
            </span>
          )}
        </span>
        {visibleSubQuestions.length > 0 && (
          <div className='mt-2 ml-2 space-y-1.5 border-l-2 border-primary/30 pl-2'>
            {visibleSubQuestions.map(sub => renderComplianceReviewCard(sub, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const nextStep = async () => {
    if (currentStep === 'type' && assessmentType) {
      setAssessmentState(prev => ({ ...prev, currentStep: 'facility' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 'facility' && selectedFacility) {
      setAssessmentState(prev => ({ ...prev, currentStep: 'general' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 'general') {
      if (isGeneralQuestionsValid()) {
        setAssessmentState(prev => ({ ...prev, currentStep: 'questions' }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error(dictionary['new_assessment.complete_general_questions']);
      }
    } else if (currentStep === 'questions') {
      const questionsReady =
        assessmentType === 'HYGIENE'
          ? answers.length > 0
          : assessmentType === 'COMPLIANCE' &&
            complianceQuestions.length > 0 &&
            allVisibleComplianceAnswered(complianceQuestions, complianceAnswers, loadedSubQuestions);
      if (questionsReady) {
        setAssessmentState(prev => ({ ...prev, currentStep: 'review' }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const prevStep = () => {
    if (currentStep === 'facility') {
      setAssessmentState(prev => ({ ...prev, currentStep: 'type' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 'general') {
      setAssessmentState(prev => ({ ...prev, currentStep: 'facility' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 'questions') {
      setAssessmentState(prev => ({ ...prev, currentStep: 'general' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 'review') {
      setAssessmentState(prev => ({ ...prev, currentStep: 'questions' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Counts the number of compliance questions that should currently be answered
  // using the same visibility rules as rendering (skip/trigger/show-if).
  const getVisibleComplianceCount = (
    qs: ComplianceQuestionTreeDto[],
    answers: ComplianceResponseItemDto[],
    loadedSubsMap: Record<string, ComplianceQuestionTreeDto[]>
  ): number => {
    return getVisibleComplianceQuestionIds(qs, answers, loadedSubsMap).size;
  };

  // Returns true only if every currently visible compliance question has an answer.
  // Uses visibility graph instead of loadedSubQuestions to correctly handle skip logic.
  const allVisibleComplianceAnswered = (
    qs: ComplianceQuestionTreeDto[],
    answers: ComplianceResponseItemDto[],
    loadedSubsMap: Record<string, ComplianceQuestionTreeDto[]>
  ): boolean => {
    const visibleIds = getVisibleComplianceQuestionIds(qs, answers, loadedSubsMap);
    const answeredIds = new Set(answers.map(a => a.questionId));
    for (const id of visibleIds) {
      if (!answeredIds.has(id)) return false;
    }
    return true;
  };

  const canProceed = () => {
    if (currentStep === 'facility')
      return (
        selectedFacilityTypeId &&
        checklistAvailable !== false &&
        selectedFacility
      );
    if (currentStep === 'type') return assessmentType !== null;
    if (currentStep === 'general') return isGeneralQuestionsValid();
    if (currentStep === 'questions') {
      if (assessmentType === 'HYGIENE') return areAllQuestionsAnswered();
      if (assessmentType === 'COMPLIANCE') {
        return (
          complianceQuestions.length > 0 &&
          !loadingCompliance &&
          allVisibleComplianceAnswered(complianceQuestions, complianceAnswers, loadedSubQuestions)
        );
      }
    }
    return true;
  };

  const handleOpenModal = () => {
    setShowCreateFacilityModal(true);
    // Pre-fill the facility type
    facilitySetValue('facilityTypeId', selectedFacilityTypeId || '');
  };

  const handleCloseModal = () => {
    setShowCreateFacilityModal(false);
    facilityReset();
    setSelectedLocationId('');
  };

  const renderRequirementCard = (type: RequirementType, title: string, description: string) => {
    const requirement = getRequirement(type);
    const isAvailable = requirement?.isAvailable;

    return (
      <div key={`requirement-${type}`} className='border border-gray-200 rounded-lg p-3 sm:p-6 bg-white'>
        <div className='space-y-3 sm:space-y-4'>
          <div>
            <h3 className='text-sm sm:text-base font-semibold text-gray-900 mb-1'>{title}</h3>
            <p className='text-xs sm:text-sm text-gray-600'>{description}</p>
          </div>

          <div className='space-y-2'>
            <label
              className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
              style={{
                borderColor: isAvailable === true ? '#1b6b45' : '#e5e7eb',
                backgroundColor: isAvailable === true ? '#eaf4ee' : 'white',
              }}
            >
              <input
                type='radio'
                checked={isAvailable === true}
                onChange={() => updateRequirement(type, { isAvailable: true })}
                className='w-4 h-4 text-primary'
              />
              <span className='ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-900'>
                {dictionary['new_assessment.req_yes']}
              </span>
            </label>
            <label
              className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
              style={{
                borderColor: isAvailable === false ? '#1b6b45' : '#e5e7eb',
                backgroundColor: isAvailable === false ? '#eaf4ee' : 'white',
              }}
            >
              <input
                type='radio'
                checked={isAvailable === false}
                onChange={() => updateRequirement(type, { isAvailable: false })}
                className='w-4 h-4 text-primary'
              />
              <span className='ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-900'>
                {dictionary['new_assessment.req_no']}
              </span>
            </label>
          </div>

          {isAvailable === false && (
            <div className='mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 space-y-3 sm:space-y-4'>
              <h4 className='text-xs sm:text-sm font-semibold text-gray-900'>
                {dictionary['new_assessment.req_additional_info']}
              </h4>

              <div>
                <label className='block text-xs sm:text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.req_has_land']}
                </label>
                <div className='space-y-2'>
                  <label
                    className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
                    style={{
                      borderColor: requirement?.hasLand === true ? '#1b6b45' : '#e5e7eb',
                      backgroundColor: requirement?.hasLand === true ? '#eaf4ee' : 'white',
                    }}
                  >
                    <input
                      type='radio'
                      checked={requirement?.hasLand === true}
                      onChange={() => updateRequirement(type, { ...requirement, hasLand: true })}
                      className='w-4 h-4 text-primary'
                    />
                    <span className='ml-2 sm:ml-3 text-xs sm:text-sm text-gray-900'>
                      {dictionary['new_assessment.req_yes']}
                    </span>
                  </label>
                  <label
                    className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
                    style={{
                      borderColor: requirement?.hasLand === false ? '#1b6b45' : '#e5e7eb',
                      backgroundColor: requirement?.hasLand === false ? '#eaf4ee' : 'white',
                    }}
                  >
                    <input
                      type='radio'
                      checked={requirement?.hasLand === false}
                      onChange={() => updateRequirement(type, { ...requirement, hasLand: false })}
                      className='w-4 h-4 text-primary'
                    />
                    <span className='ml-2 sm:ml-3 text-xs sm:text-sm text-gray-900'>
                      {dictionary['new_assessment.req_no']}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className='block text-xs sm:text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.req_has_funds']}
                </label>
                <div className='space-y-2'>
                  <label
                    className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
                    style={{
                      borderColor: requirement?.hasFunds === true ? '#1b6b45' : '#e5e7eb',
                      backgroundColor: requirement?.hasFunds === true ? '#eaf4ee' : 'white',
                    }}
                  >
                    <input
                      type='radio'
                      checked={requirement?.hasFunds === true}
                      onChange={() => updateRequirement(type, { ...requirement, hasFunds: true })}
                      className='w-4 h-4 text-primary'
                    />
                    <span className='ml-2 sm:ml-3 text-xs sm:text-sm text-gray-900'>
                      {dictionary['new_assessment.req_yes']}
                    </span>
                  </label>
                  <label
                    className='flex items-center p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors'
                    style={{
                      borderColor: requirement?.hasFunds === false ? '#1b6b45' : '#e5e7eb',
                      backgroundColor: requirement?.hasFunds === false ? '#eaf4ee' : 'white',
                    }}
                  >
                    <input
                      type='radio'
                      checked={requirement?.hasFunds === false}
                      onChange={() => updateRequirement(type, { ...requirement, hasFunds: false })}
                      className='w-4 h-4 text-primary'
                    />
                    <span className='ml-2 sm:ml-3 text-xs sm:text-sm text-gray-900'>
                      {dictionary['new_assessment.req_no']}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className='block text-xs sm:text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.req_estimated_completion']}
                </label>
                <Input
                  type='date'
                  value={requirement?.estimatedCompletion || ''}
                  onChange={e => updateRequirement(type, { ...requirement, estimatedCompletion: e.target.value })}
                  className='text-sm'
                />
              </div>

              <div>
                <label className='block text-xs sm:text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.req_comment']}
                </label>
                <textarea
                  value={requirement?.notes || ''}
                  onChange={e => updateRequirement(type, { ...requirement, notes: e.target.value })}
                  placeholder={dictionary['new_assessment.req_comment_placeholder']}
                  rows={3}
                  className='w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact Photo Gallery Component - shows on every step
  const CompactPhotoSection = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    // Lower z-index on review step to not block submit button
    const zIndex = currentStep === 'review' ? 'z-30' : 'z-40';

    return (
      <div className={`sticky bottom-0 left-0 right-0 ${zIndex} bg-white border-t-2 border-primary`}>
        {/* Collapsed Header - Always Visible */}
        <div
          className='flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 cursor-pointer'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className='flex items-center gap-2 sm:gap-3'>
            <div className='bg-primary text-white rounded-full p-1.5 sm:p-2'>
              <Camera className='h-4 w-4 sm:h-5 sm:w-5' />
            </div>
            <div>
              <h4 className='text-xs sm:text-sm font-semibold text-gray-900'>
                {dictionary['new_assessment.assessment_photos']}
              </h4>
              <p className='text-xs text-gray-600'>
                {uploadedImages.length}{' '}
                {uploadedImages.length !== 1 ? dictionary['new_assessment.photos'] : dictionary['new_assessment.photo']}
              </p>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Button
              type='button'
              onClick={e => {
                e.stopPropagation();
                handleCaptureImage();
              }}
              disabled={isUploading}
              size='sm'
              className='h-8 sm:h-9 px-2 sm:px-3'
            >
              {isUploading ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <>
                  <Camera className='h-4 w-4' />
                  <span className='hidden sm:inline ml-2 text-xs'>
                    {dictionary['new_assessment.add_photo'] || 'Add'}
                  </span>
                </>
              )}
            </Button>
            <button
              type='button'
              className='text-gray-400'
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
              </svg>
            </button>
          </div>
        </div>

        {/* Expanded Content - Shows when toggled */}
        {isExpanded && (
          <div className='border-t border-gray-200 px-3 sm:px-4 py-3 sm:py-4 max-h-64 overflow-y-auto bg-gray-50'>
            {isUploading && (
              <div className='flex items-center justify-center py-6 border border-primary/30 rounded-lg bg-secondary mb-3'>
                <Loader2 className='h-6 w-6 animate-spin text-primary mr-2' />
                <span className='text-sm text-primary'>{dictionary['new_assessment.capturing_uploading']}</span>
              </div>
            )}

            {uploadedImages.length === 0 && !isUploading ? (
              <div className='text-center py-6 border border-gray-200 rounded-lg bg-white'>
                <Camera className='h-10 w-10 text-gray-400 mx-auto mb-2' />
                <p className='text-sm text-gray-600 mb-3'>{dictionary['new_assessment.no_photos_yet']}</p>
                <Button type='button' onClick={handleCaptureImage} size='sm'>
                  <Camera className='h-4 w-4 mr-2' />
                  {dictionary['new_assessment.capture_first_photo']}
                </Button>
              </div>
            ) : (
              <div className='grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2'>
                {uploadedImages.map((image, index) => (
                  <div
                    key={`image-${image.id}`}
                    className='relative group bg-white rounded-lg border border-gray-200 overflow-hidden aspect-square'
                  >
                    <img
                      src={image.url}
                      alt={`${dictionary['new_assessment.assessment_photo_alt']} ${index + 1}`}
                      className='w-full h-full object-cover cursor-pointer'
                      onClick={() => window.open(image.url, '_blank')}
                    />
                    <div className='absolute top-1 left-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium'>
                      {index + 1}
                    </div>
                    <button
                      type='button'
                      onClick={() => handleDeleteImage(image)}
                      className='absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center'
                    >
                      <Trash2 className='h-3 w-3' />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Full Photo Gallery for Review Step
  const PhotoGallery = () => (
    <div key='photo-gallery' className='space-y-3'>
      {isUploading && (
        <div className='flex items-center justify-center py-6 border border-gray-200 rounded bg-gray-50'>
          <Loader2 className='h-5 w-5 animate-spin text-gray-600 mr-2' />
          <p className='text-sm text-gray-600'>{dictionary['new_assessment.capturing_uploading']}</p>
        </div>
      )}

      {uploadedImages.length === 0 && !isUploading ? (
        <div className='flex flex-col items-center justify-center py-8 text-center border border-dashed border-gray-300 rounded bg-gray-50'>
          <Camera className='h-10 w-10 text-gray-400 mb-2' />
          <p className='text-sm text-gray-600 mb-3'>{dictionary['new_assessment.no_photos_yet']}</p>
          <Button type='button' onClick={handleCaptureImage} size='sm'>
            <Camera className='h-4 w-4 mr-2' />
            {dictionary['new_assessment.capture_first_photo']}
          </Button>
        </div>
      ) : (
        <div className='space-y-3'>
          <Button
            type='button'
            onClick={handleCaptureImage}
            disabled={isUploading}
            className='w-full text-sm'
            size='sm'
          >
            {isUploading ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                {dictionary['new_assessment.processing']}
              </>
            ) : (
              <>
                <Camera className='h-4 w-4 mr-2' />
                {dictionary['new_assessment.add_another_photo']}
              </>
            )}
          </Button>

          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2'>
            {uploadedImages.map((image, index) => (
              <div
                key={`image-${image.id}`}
                className='relative group bg-white rounded border border-gray-200 overflow-hidden'
              >
                <img
                  src={image.url}
                  alt={`Photo ${index + 1}`}
                  className='w-full h-24 object-cover cursor-pointer'
                  onClick={() => window.open(image.url, '_blank')}
                />
                <div className='absolute top-1 left-1 bg-gray-900 bg-opacity-70 text-white text-xs rounded px-1.5 py-0.5'>
                  {index + 1}
                </div>
                <button
                  type='button'
                  onClick={() => handleDeleteImage(image)}
                  className='absolute top-1 right-1 bg-red-600 text-white rounded w-6 h-6 flex items-center justify-center'
                  aria-label='Delete'
                >
                  <Trash2 className='h-3 w-3' />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!userCanCreateAssessment) {
    return (
      <div className='p-6'>
        <Card>
          <CardContent className='p-6'>
            <p className='text-red-600'>{dictionary['new_assessment.access_denied']}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps = [
    { key: 'type', label: dictionary['new_assessment.step_type'] || 'Assessment Type' },
    { key: 'facility', label: dictionary['new_assessment.step_facility'] || 'Facility' },
    { key: 'general', label: dictionary['new_assessment.step_questions_1'] || 'General Questions' },
    { key: 'questions', label: dictionary['new_assessment.step_questions_2'] || 'Assessment Questions' },
    { key: 'review', label: dictionary['new_assessment.step_review'] || 'Review' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className={`p-3 sm:p-6 space-y-4 sm:space-y-6 ${currentStep === 'questions' ? 'pb-24 sm:pb-32' : ''}`}>
      <div className='mb-4 sm:mb-8'>
        <h1 className='text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2'>
          {dictionary['new_assessment.title']}
        </h1>
        <p className='text-sm sm:text-base text-gray-600'>{dictionary['new_assessment.subtitle']}</p>
      </div>

      {/* Draft Recovery Banner */}
      {showDraftBanner && savedDraft && (
        <div className='bg-amber-50 border-l-4 border-amber-400 p-3 sm:p-4 mb-4 sm:mb-6 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300'>
          <div className='flex items-start gap-2 sm:gap-3'>
            <div className='flex-shrink-0'>
              <svg
                className='h-4 w-4 sm:h-5 sm:w-5 text-amber-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>
            <div className='flex-1 min-w-0'>
              <h3 className='text-xs sm:text-sm font-semibold text-amber-900'>
                {dictionary['new_assessment.draft_found']}
              </h3>
              <div className='mt-1 sm:mt-2'>
                <p className='text-xs sm:text-sm text-amber-700 hidden sm:block'>
                  {dictionary['new_assessment.draft_message']}
                </p>
                <p className='text-xs text-amber-700 sm:hidden'>{dictionary['new_assessment.draft_message_mobile']}</p>
                <p className='mt-1 text-xs text-amber-600'>
                  {dictionary['new_assessment.last_saved']}:{' '}
                  <span className='hidden sm:inline'>{new Date(savedDraft.timestamp).toLocaleString()}</span>
                  <span className='sm:hidden'>{new Date(savedDraft.timestamp).toLocaleDateString()}</span>
                  {savedDraft.currentStep && (
                    <span className='ml-1 sm:ml-2 hidden sm:inline'>
                      • {dictionary['new_assessment.step']}: {steps.find(s => s.key === savedDraft.currentStep)?.label}
                    </span>
                  )}
                </p>
              </div>
              <div className='mt-2 sm:mt-3 flex flex-col sm:flex-row gap-2'>
                <Button
                  type='button'
                  onClick={handleRestoreDraft}
                  size='sm'
                  disabled={isRestoringDraft}
                  className='bg-amber-600 text-white h-8 text-xs'
                >
                  {isRestoringDraft ? (
                    <>
                      <Loader2 className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin' />
                      <span className='hidden sm:inline'>
                        {dictionary['new_assessment.restoring'] || 'Restoring...'}
                      </span>
                      <span className='sm:hidden'>{dictionary['new_assessment.restoring_mobile'] || 'Loading...'}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                        />
                      </svg>
                      <span className='hidden sm:inline'>{dictionary['new_assessment.resume_draft']}</span>
                      <span className='sm:hidden'>{dictionary['new_assessment.resume_draft_mobile']}</span>
                    </>
                  )}
                </Button>
                <Button
                  type='button'
                  onClick={handleDiscardDraft}
                  variant='outline'
                  size='sm'
                  className='border-amber-300 text-amber-700 h-8 text-xs'
                >
                  <X className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2' />
                  <span className='hidden sm:inline'>{dictionary['new_assessment.start_fresh']}</span>
                  <span className='sm:hidden'>{dictionary['new_assessment.start_fresh_mobile']}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='flex items-center mb-8 w-full'>
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div className='flex items-center flex-shrink-0'>
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  index < currentStepIndex
                    ? 'bg-green-600 border-green-600 text-white'
                    : index === currentStepIndex
                      ? 'bg-primary border-primary text-white'
                      : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {index < currentStepIndex ? (
                  <CheckCircle2 className='h-5 w-5' />
                ) : (
                  <span className='text-sm font-semibold'>{index + 1}</span>
                )}
              </div>
              <span
                className={`ml-3 text-sm font-medium hidden sm:inline whitespace-nowrap ${
                  index <= currentStepIndex ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`h-0.5 flex-1 mx-4 transition-colors ${
                  index < currentStepIndex ? 'bg-green-600' : 'bg-gray-300'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Use keys on conditional renders to prevent state reset */}
      {currentStep === 'facility' && (
        <Card key='facility-step'>
          <CardHeader>
            <CardTitle>{dictionary['new_assessment.select_facility_title']}</CardTitle>
            <CardDescription>{dictionary['new_assessment.select_facility_desc']}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            {/* Step 1: Select Facility Type (same searchable select as facility) */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {dictionary['new_assessment.select_facility_type_label']} *
              </label>
              <SearchableSelect
                options={facilityTypes.map(ft => ({
                  value: ft.id,
                  label: getLocalizedFacilityTypeName(ft, locale),
                }))}
                value={selectedFacilityTypeId || ''}
                onChange={value => handleFacilityTypeChange(value || null)}
                placeholder={dictionary['facilities.select_facility_type'] || 'Select facility type'}
                loading={facilityTypesLoading}
                disabled={facilityTypesLoading}
              />
              {selectedFacilityTypeId && checklistAvailable === false && (
                <div className='mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3'>
                  <AlertCircle className='h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0' />
                  <p className='text-sm text-amber-800'>
                    {dictionary['new_assessment.no_checklist_available'] ??
                      'No active checklist is configured for this facility type. Contact your administrator.'}
                  </p>
                </div>
              )}
            </div>

            {/* Subcategory filter — only when the selected facility type has subcategories */}
            {selectedFacilityTypeId && (() => {
              const ft = facilityTypes.find(f => f.id === selectedFacilityTypeId);
              const subs = ft?.subcategories ?? [];
              if (!subs.length) return null;
              return (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Subcategory
                  </label>
                  <select
                    value={selectedFilterSubcategoryId}
                    onChange={e => {
                      setSelectedFilterSubcategoryId(e.target.value);
                      setAssessmentState(prev => ({ ...prev, selectedFacility: '' }));
                      assessmentSetValue('facilityId', '');
                    }}
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  >
                    <option value=''>All subcategories</option>
                    {subs.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {/* Location filter — shown once a facility type is selected */}
            {selectedFacilityTypeId && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Filter by Location
                </label>
                <div className='flex items-center gap-2'>
                  <div className='flex-1'>
                    <SingleLocationSelector
                      key={facilityFilterResetKey}
                      compact
                      selectedLocationId={facilityFilterLocationId}
                      initialHierarchy={locationHierarchy ? {
                        provinceId: locationHierarchy.provinceId,
                        districtId: locationHierarchy.districtId,
                        sectorId: locationHierarchy.sectorId,
                        cellId: locationHierarchy.cellId,
                        villageId: locationHierarchy.villageId,
                      } : undefined}
                      onLocationChange={id => {
                        setFacilityFilterLocationId(id || undefined);
                        setAssessmentState(prev => ({ ...prev, selectedFacility: '' }));
                        assessmentSetValue('facilityId', '');
                      }}
                    />
                  </div>
                  {facilityFilterLocationId && (currentUser as any)?.isCentralLevel && (
                    <button
                      type='button'
                      onClick={() => {
                        setFacilityFilterLocationId(undefined);
                        setFacilityFilterResetKey(k => k + 1);
                        setAssessmentState(prev => ({ ...prev, selectedFacility: '' }));
                        assessmentSetValue('facilityId', '');
                      }}
                      className='text-xs text-gray-400 hover:text-gray-600 flex-shrink-0'
                    >
                      Clear
                    </button>
                  )}
                </div>
                {facilityFilterLocationId && (
                  <p className='mt-1 text-xs text-gray-400'>
                    Showing facilities within selected location
                  </p>
                )}
              </div>
            )}

            {/* Step 2: Select Facility (only shown after facility type is selected) */}
            {selectedFacilityTypeId && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.select_facility_label']} *
                </label>

                <SearchableSelect
                  options={facilities
                    .filter(facility => facility.canAssess)
                    .map(facility => ({
                      value: facility.id,
                      label: facility.title,
                      subtitle: facility.reason,
                      disabled: !facility.canAssess,
                    }))}
                  value={selectedFacility}
                  onChange={handleFacilityChange}
                  placeholder={dictionary['new_assessment.select_facility_placeholder'] || 'Select a facility'}
                  onCreateNew={handleOpenModal}
                  createNewLabel={dictionary['new_assessment.create_new_facility']}
                  loading={facilitiesLoading}
                  disabled={facilitiesLoading}
                />

                {/* Show assessable facilities count */}
                {assessableFacilitiesData && (
                  <p className='mt-2 text-sm text-gray-600'>
                    {assessableFacilitiesData.assessableFacilitiesCount} of {assessableFacilitiesData.totalFacilities}{' '}
                    facilities can be assessed this week
                  </p>
                )}

                {assessmentErrors.facilityId && (
                  <p className='mt-1 text-sm text-red-600'>{assessmentErrors.facilityId.message}</p>
                )}
              </div>
            )}

            {/* Step 3: Facility GPS Coordinates (auto-populated for existing facilities) */}
            {selectedFacility && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['new_assessment.facility_coordinates_label'] || 'Facility GPS Coordinates'} *
                </label>
                {facilityCoordinates && (
                  <p className='text-xs text-green-600 mb-1 flex items-center gap-1'>
                    <CheckCircle2 className='h-3 w-3' />
                    {dictionary['new_assessment.coordinates_loaded'] || 'Coordinates loaded from facility'}
                  </p>
                )}
                <div className='grid grid-cols-[1fr_auto] gap-2'>
                  <Input
                    type='text'
                    value={facilityCoordinates}
                    onChange={e => setFacilityCoordinates(e.target.value)}
                    placeholder='-1.9441, 30.0619'
                  />
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                      setIsGettingLocation(true);
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          position => {
                            const coords = `${position.coords.latitude},${position.coords.longitude}`;
                            setFacilityCoordinates(coords);
                            setIsGettingLocation(false);
                            toast.success(
                              dictionary['new_assessment.coordinates_captured'] ||
                                'GPS coordinates captured successfully'
                            );
                          },
                          error => {
                            console.error('Error getting location:', error);
                            toast.error(
                              dictionary['new_assessment.coordinates_error'] ||
                                'Could not get GPS location. Please enter manually.'
                            );
                            setIsGettingLocation(false);
                          },
                          {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 0,
                          }
                        );
                      } else {
                        toast.error(dictionary['new_assessment.gps_not_supported'] || 'GPS is not supported');
                        setIsGettingLocation(false);
                      }
                    }}
                    disabled={isGettingLocation}
                    className='whitespace-nowrap'
                    title={dictionary['new_assessment.update_coordinates'] || 'Update coordinates if needed'}
                  >
                    {isGettingLocation ? (
                      <>
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        {dictionary['new_assessment.getting_location'] || 'Getting Location...'}
                      </>
                    ) : (
                      <>
                        <Navigation className='h-4 w-4 mr-2' />
                        {facilityCoordinates
                          ? dictionary['new_assessment.update_location'] || 'Update'
                          : dictionary['new_assessment.use_gps'] || 'Get Location'}
                      </>
                    )}
                  </Button>
                </div>
                {facilityCoordinates && !/^-?\d+\.?\d*,-?\d+\.?\d*$/.test(facilityCoordinates.trim()) && (
                  <p className='mt-2 text-sm text-red-600'>
                    {dictionary['new_assessment.invalid_coordinate_format'] ||
                      'Invalid format. Use: latitude,longitude (e.g., -1.9441,30.0619)'}
                  </p>
                )}
                {facilityCoordinates && /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(facilityCoordinates.trim()) && (
                  <div className='mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2'>
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                    <p className='text-sm text-green-700'>
                      {dictionary['new_assessment.coordinates_captured'] || 'GPS coordinates captured successfully'}
                    </p>
                  </div>
                )}
                {!facilityCoordinates && (
                  <p className='mt-2 text-sm text-gray-600'>
                    {dictionary['new_assessment.coordinates_helper'] ||
                      'Click "Get Location" to automatically get coordinates or enter manually'}
                  </p>
                )}
              </div>
            )}

            {/* Previous Assessment Glimpse */}
            {selectedFacility && (
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                  {dictionary['new_assessment.previous_assessment']}
                </p>
                {loadingPreviousAssessment ? (
                  <div className='flex items-center gap-2 text-xs text-gray-500'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    {dictionary['common.loading'] || 'Loading...'}
                  </div>
                ) : previousAssessment ? (
                  <>
                  <div className='grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs'>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_score']} </span>
                      <span className='font-semibold text-gray-900'>
                        {previousAssessment.percentageScore.toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_decision']} </span>
                      <span
                        className={`font-semibold ${
                          previousAssessment.finalDecision === 'COMPLIANT' ||
                          previousAssessment.finalDecision === 'NO_ACTION'
                            ? 'text-green-700'
                            : 'text-red-700'
                        }`}
                      >
                        {previousAssessment.finalDecision.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_date']} </span>
                      <span className='font-semibold text-gray-900'>
                        {new Date(previousAssessment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                          previousAssessment.isClean ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {previousAssessment.isClean ? (
                          <CheckCircle2 className='h-3 w-3' />
                        ) : (
                          <AlertCircle className='h-3 w-3' />
                        )}
                        {previousAssessment.isClean
                          ? dictionary['new_assessment.clean'] || 'Clean'
                          : dictionary['new_assessment.not_clean'] || 'Not Clean'}
                      </span>
                    </div>
                  </div>
                  {(pendingRecommendations?.recommendations?.length ?? 0) > 0 && (
                    <div className='mt-2 pt-2 border-t border-gray-200'>
                      <p className='text-xs font-medium text-gray-500 mb-1 flex items-center gap-1'>
                        <MessageSquare className='h-3 w-3' />
                        {dictionary['new_assessment.recommendations']}
                      </p>
                      <ul className='space-y-1'>
                        {pendingRecommendations!.recommendations.map(rec => (
                          <li key={rec.id} className='flex items-start gap-2 text-xs'>
                            <span className={`mt-0.5 flex-shrink-0 ${rec.resolved ? 'text-green-600' : 'text-amber-500'}`}>
                              {rec.resolved ? <CheckCircle2 className='h-3 w-3' /> : <Clock className='h-3 w-3' />}
                            </span>
                            <span className={rec.resolved ? 'line-through text-gray-400' : 'text-gray-700'}>
                              {rec.recommendation}
                              {rec.deadline && (
                                <span className='ml-1 text-gray-400'>
                                  · {new Date(rec.deadline).toLocaleDateString()}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  </>
                ) : (
                  <p className='text-xs text-gray-500 italic'>
                    {dictionary['new_assessment.no_previous_assessment'] || 'No previous assessment found'}
                  </p>
                )}
              </div>
            )}

            <div className='flex justify-end pt-4 border-t border-gray-200'>
              <Button type='button' onClick={nextStep} disabled={!canProceed()}>
                <span className='hidden sm:inline'>{dictionary['new_assessment.next_general_info'] ?? 'Next: General Info'}</span>
                <span className='sm:hidden'>{dictionary['new_assessment.next_general_info_short'] ?? 'Next'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 'type' && (
        <Card key='type-step'>
          <CardHeader>
            <CardTitle>{dictionary['new_assessment.choose_assessment_type_title'] ?? 'Choose assessment type'}</CardTitle>
            <CardDescription>
              {dictionary['new_assessment.choose_assessment_type_desc'] ??
                'Start a hygiene assessment (questionnaire) or a compliance assessment. You can create both for the same facility in the same week.'}
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              {(() => {
                const hygieneUnavailable = !hygieneAvailabilityLoading && hygieneAvailability?.available === false;
                return (
                  <button
                    type='button'
                    disabled={hygieneUnavailable}
                    onClick={() => {
                      setAssessmentState(prev => ({ ...prev, assessmentType: 'HYGIENE', currentStep: 'facility' }));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`relative p-6 rounded-xl border-2 text-left transition-colors w-full flex flex-col gap-2 ${
                      hygieneUnavailable
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70'
                        : assessmentType === 'HYGIENE'
                          ? 'border-primary bg-secondary'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div>
                      <h3 className='font-semibold text-gray-900'>
                        {dictionary['new_assessment.type_hygiene'] ?? 'Hygiene assessment'}
                      </h3>
                      <p className='text-sm text-gray-600 mt-1'>
                        {dictionary['new_assessment.type_hygiene_desc'] ??
                          'Answer the hygiene questionnaire for this facility.'}
                      </p>
                    </div>
                    {hygieneUnavailable && (
                      <div className='flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-medium px-3 py-2 rounded-lg w-full'>
                        <AlertCircle className='h-4 w-4 flex-shrink-0' />
                        {dictionary['new_assessment.checklist_not_available'] ?? 'Checklist not available'}
                      </div>
                    )}
                  </button>
                );
              })()}
              {(() => {
                const complianceUnavailable = !complianceAvailabilityLoading && complianceAvailability?.available === false;
                return (
                  <button
                    type='button'
                    disabled={complianceUnavailable}
                    onClick={() => {
                      setAssessmentState(prev => ({ ...prev, assessmentType: 'COMPLIANCE', currentStep: 'facility' }));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`relative p-6 rounded-xl border-2 text-left transition-colors w-full flex flex-col gap-2 ${
                      complianceUnavailable
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70'
                        : assessmentType === 'COMPLIANCE'
                          ? 'border-primary bg-secondary'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div>
                      <h3 className='font-semibold text-gray-900'>
                        {dictionary['new_assessment.type_compliance'] ?? 'Compliance assessment'}
                      </h3>
                      <p className='text-sm text-gray-600 mt-1'>
                        {dictionary['new_assessment.type_compliance_desc'] ??
                          'Answer compliance questions for this facility type.'}
                      </p>
                    </div>
                    {complianceUnavailable && (
                      <div className='flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-medium px-3 py-2 rounded-lg w-full'>
                        <AlertCircle className='h-4 w-4 flex-shrink-0' />
                        {dictionary['new_assessment.checklist_not_available'] ?? 'Checklist not available'}
                      </div>
                    )}
                  </button>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 'general' && (
        <div key='general-step' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>{dictionary['new_assessment.general_requirements']}</CardTitle>
              <CardDescription>{dictionary['new_assessment.answer_general_questions']}</CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {isHomeFacility ? (
                <>
                  {renderRequirementCard(
                    RequirementType.STANDARD_TOILET,
                    dictionary['new_assessment.req_standard_toilet_title'],
                    dictionary['new_assessment.req_standard_toilet_desc']
                  )}
                  {renderRequirementCard(
                    RequirementType.STANDARD_HOUSE,
                    dictionary['new_assessment.req_standard_house_title'],
                    dictionary['new_assessment.req_standard_house_desc']
                  )}
                </>
              ) : (
                <>
                  {renderRequirementCard(
                    RequirementType.HANDWASHING_FACILITY,
                    dictionary['new_assessment.req_handwashing_title'],
                    dictionary['new_assessment.req_handwashing_desc']
                  )}
                  {renderRequirementCard(
                    RequirementType.CLEAN_WATER_FACILITY,
                    dictionary['new_assessment.req_clean_water_title'],
                    dictionary['new_assessment.req_clean_water_desc']
                  )}
                  {renderRequirementCard(
                    RequirementType.WASTE_MANAGEMENT_FACILITY,
                    dictionary['new_assessment.req_waste_management_title'],
                    dictionary['new_assessment.req_waste_management_desc']
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className='flex justify-between gap-2'>
            <Button type='button' variant='outline' onClick={prevStep}>
              <span className='hidden sm:inline'>{dictionary['new_assessment.back_to_facility'] ?? 'Back to facility'}</span>
              <span className='sm:hidden'>{dictionary['new_assessment.back_to_facility_short'] ?? 'Back'}</span>
            </Button>
            <Button type='button' onClick={nextStep} disabled={!canProceed()}>
              <span className='hidden sm:inline'>{dictionary['new_assessment.next_assessment_questions']}</span>
              <span className='sm:hidden'>{dictionary['new_assessment.next_assessment_questions_short']}</span>
            </Button>
          </div>
        </div>
      )}

      {currentStep === 'questions' && (
        <>
          <div key='questions-step' className='space-y-6'>
            {/* Hygiene: questionnaire only */}
            {assessmentType === 'HYGIENE' && (
              <Card>
                <CardHeader>
                  <CardTitle>{dictionary['new_assessment.assessment_questions_title']}</CardTitle>
                  <CardDescription>{dictionary['new_assessment.assessment_questions_desc']}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingQuestionnaire ? (
                    <div className='flex items-center gap-2 py-6 text-sm text-gray-500'>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      {dictionary['new_assessment.loading_questionnaire']}
                    </div>
                  ) : (
                    <QuestionnaireForm
                      questionnaire={questionnaire}
                      onAnswersChange={handleAnswersChange}
                      initialAnswers={answers}
                      emptyStateMessage={questionnaireEmptyMessage || undefined}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Compliance: compliance questions only (all required) */}
            {assessmentType === 'COMPLIANCE' && (
              <>
                <FullScreenPdfViewer
                  open={showComplianceDocumentViewer}
                  onOpenChange={setShowComplianceDocumentViewer}
                  documents={complianceReferenceDocuments}
                  title={dictionary['questionnaire.reference_documents']}
                />

                {loadingCompliance && (
                  <Card>
                    <CardContent className='py-6'>
                      <div className='flex items-center gap-2 text-sm text-gray-500'>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        {dictionary['new_assessment.loading_compliance'] || 'Loading compliance questions...'}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {!loadingCompliance && complianceQuestions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{dictionary['new_assessment.compliance_section_title'] || 'Compliance'}</CardTitle>
                      <CardDescription>
                        {dictionary['new_assessment.compliance_section_desc_required'] ??
                          'Answer all compliance questions below (required).'}
                      </CardDescription>
                      {complianceReferenceDocuments.length > 0 && (
                        <button
                          type='button'
                          onClick={() => setShowComplianceDocumentViewer(true)}
                          className='mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline w-fit'
                        >
                          <FileText className='h-4 w-4' /> {dictionary['questionnaire.view_documents']}
                        </button>
                      )}
                    </CardHeader>
                    <CardContent className='space-y-6'>
                      {(() => {
                        let rootIndex = 0;
                        return (
                          <>
                            {complianceRootsBySection.orderedSections.map(section => {
                              const qs = complianceRootsBySection.groupedBySection[section.id] || [];
                              if (qs.length === 0) return null;
                              const sectionTitle =
                                locale === 'rw'
                                  ? section.titleKinyarwanda || section.title
                                  : locale === 'fr'
                                    ? section.titleFrench || section.title
                                    : section.title;
                              return (
                                <div key={section.id} className='space-y-4'>
                                  <h3 className='text-base font-semibold text-gray-800 border-b-2 border-primary/20 pb-2'>
                                    {sectionTitle}
                                  </h3>
                                  {qs.map(q => renderComplianceQuestion(q, rootIndex++, 0))}
                                </div>
                              );
                            })}
                            {complianceRootsBySection.unsectionedRoots.length > 0 && (
                              <div className='space-y-4'>
                                {complianceRootsBySection.unsectionedRoots.map(q =>
                                  renderComplianceQuestion(q, rootIndex++, 0)
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}

                {!loadingCompliance && complianceQuestions.length === 0 && questionnaireEmptyMessage && (
                  <Card>
                    <CardContent className='py-6'>
                      <div className='text-sm text-gray-600 text-center'>{questionnaireEmptyMessage}</div>
                    </CardContent>
                  </Card>
                )}

                {/* Photos — inline section matching hygiene behaviour */}
                {!loadingCompliance && (
                  <Card>
                    <CardHeader className='pb-2'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <Camera className='h-4 w-4 text-primary' />
                          <CardTitle className='text-sm'>{dictionary['new_assessment.assessment_photos']}</CardTitle>
                          <span className='text-xs text-gray-500'>({uploadedImages.length})</span>
                        </div>
                        <Button
                          type='button'
                          size='sm'
                          onClick={handleCaptureImage}
                          disabled={isUploading}
                          className='h-8 px-2'
                        >
                          {isUploading ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <>
                              <Camera className='h-4 w-4' />
                              <span className='ml-1 text-xs hidden sm:inline'>
                                {dictionary['new_assessment.add_photo'] || 'Add'}
                              </span>
                            </>
                          )}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className='pt-1'>
                      <PhotoGallery />
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            <div className='space-y-3'>
              {/* Warning: hygiene – not all questions answered */}
              {assessmentType === 'HYGIENE' && !canProceed() && answers.length > 0 && questionnaire?.questions && (
                <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                  <div className='flex items-start gap-2'>
                    <div className='text-amber-600 mt-0.5'>⚠️</div>
                    <div>
                      <p className='text-sm font-medium text-amber-800'>
                        Please answer all questions before proceeding
                      </p>
                      <p className='text-xs text-amber-700 mt-1'>
                        {(() => {
                          const selectedOptionIds = new Set(answers.map(a => a.selectedOptionId));
                          const mainQuestions = questionnaire.questions.filter(
                            q => !q.skipIfOptionId || !selectedOptionIds.has(q.skipIfOptionId)
                          );
                          const mainQuestionIds = mainQuestions.map(q => q.id);
                          const answeredMainQuestions = answers.filter(a => mainQuestionIds.includes(a.questionId));
                          return `You have answered ${answeredMainQuestions.length} of ${mainQuestions.length} main questions`;
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Warning: compliance – not all answered */}
              {assessmentType === 'COMPLIANCE' && complianceQuestions.length > 0 && !canProceed() && (
                <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                  <div className='flex items-start gap-2'>
                    <div className='text-amber-600 mt-0.5'>⚠️</div>
                    <div>
                      <p className='text-sm font-medium text-amber-800'>
                        {dictionary['new_assessment.compliance_all_required'] ??
                          'Please answer all compliance questions before proceeding.'}
                      </p>
                      <p className='text-xs text-amber-700 mt-1'>
                        {filterComplianceAnswersByVisibility(complianceQuestions, complianceAnswers, loadedSubQuestions)
                          .length}{' '}
                        of {getVisibleComplianceCount(complianceQuestions, complianceAnswers, loadedSubQuestions)}{' '}
                        answered
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className='flex justify-between gap-2'>
                <Button type='button' variant='outline' onClick={prevStep}>
                  <span className='hidden sm:inline'>{dictionary['new_assessment.back_to_general_questions']}</span>
                  <span className='sm:hidden'>{dictionary['new_assessment.back_to_general_questions_short']}</span>
                </Button>
                <Button type='button' onClick={nextStep} disabled={!canProceed()}>
                  <span className='hidden sm:inline'>{dictionary['new_assessment.next_review']}</span>
                  <span className='sm:hidden'>{dictionary['new_assessment.next_review_short']}</span>
                </Button>
              </div>
            </div>
          </div>
          <CompactPhotoSection />
        </>
      )}

      {currentStep === 'review' && (
        <form key='review-step' onSubmit={handleAssessmentSubmit(onAssessmentSubmit, onAssessmentInvalid)} className='space-y-3 pb-24'>
          {/* Header */}
          <div className='mb-3'>
            <h2 className='text-sm font-semibold text-gray-900'>{dictionary['new_assessment.review_title']}</h2>
            <p className='text-xs text-gray-600 mt-0.5'>{dictionary['new_assessment.review_desc']}</p>
          </div>

          {/* Facility Information */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.facility_information']}
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 text-xs'>
                {(() => {
                  const facility = facilities?.find(f => f.id === selectedFacility);
                  return (
                    <>
                      <div>
                        <span className='text-gray-500'>{dictionary['new_assessment.facility_name']} </span>
                        <span className='font-medium text-gray-900'>{facility?.title}</span>
                      </div>
                      <div>
                        <span className='text-gray-500'>{dictionary['new_assessment.facility_type']} </span>
                        <span className='font-medium text-gray-900'>
                          {locale === 'rw'
                            ? facility?.facilityType?.nameKinyarwanda
                            : locale === 'fr'
                              ? facility?.facilityType?.nameFrench
                              : facility?.facilityType?.name}
                        </span>
                      </div>
                      {facility?.identifierNumber && (
                        <div>
                          <span className='text-gray-500'>{dictionary['new_assessment.facility_id']} </span>
                          <span className='font-medium text-gray-900'>{facility.identifierNumber}</span>
                        </div>
                      )}
                      {facilityCoordinates && (
                        <div className='sm:col-span-2 lg:col-span-3'>
                          <span className='text-gray-500'>{dictionary['new_assessment.facility_gps']} </span>
                          <span className='font-medium text-gray-900 font-mono text-xs'>{facilityCoordinates}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* General Requirements */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.general_requirements_section']}
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                {requirements.map(req => (
                  <div key={`review-${req.requirementType}`} className='border border-gray-200 rounded p-2 text-xs'>
                    <div className='flex justify-between items-start'>
                      <span className='font-medium text-gray-900'>{req.requirementType.replace(/_/g, ' ')}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          req.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {req.isAvailable
                          ? dictionary['new_assessment.available']
                          : dictionary['new_assessment.not_available']}
                      </span>
                    </div>
                    {!req.isAvailable && (
                      <div className='mt-1 pt-1 border-t border-gray-100 space-y-1'>
                        <div className='grid grid-cols-2 gap-1'>
                          <div>
                            <span className='text-gray-500'>{dictionary['new_assessment.requirement_land']} </span>
                            <span className='font-medium'>
                              {req.hasLand ? dictionary['new_assessment.req_yes'] : dictionary['new_assessment.req_no']}
                            </span>
                          </div>
                          <div>
                            <span className='text-gray-500'>{dictionary['new_assessment.requirement_funds']} </span>
                            <span className='font-medium'>
                              {req.hasFunds
                                ? dictionary['new_assessment.req_yes']
                                : dictionary['new_assessment.req_no']}
                            </span>
                          </div>
                        </div>
                        {req.estimatedCompletion && (
                          <div>
                            <span className='text-gray-500'>
                              {dictionary['new_assessment.requirement_est_completion']}{' '}
                            </span>
                            <span className='font-medium'>
                              {new Date(req.estimatedCompletion).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {req.notes && (
                          <div className='mt-1 bg-gray-50 p-1 rounded'>
                            <p className='text-gray-700'>{req.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Questions and Answers (Hygiene only) */}
          {assessmentType === 'HYGIENE' && (
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>{dictionary['new_assessment.questions_answers']}</CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'>
                {questionnaire?.questions?.map((question, index) => {
                  const answer = answers.find(a => a.questionId === question.id);
                  const selectedOption = question.facilityAssessmentQuestionOptions?.find(
                    opt => opt.id === answer?.selectedOptionId
                  );
                  const hasSubquestions =
                    answer &&
                    reviewSubquestions[answer.selectedOptionId] &&
                    reviewSubquestions[answer.selectedOptionId].length > 0;

                  return (
                    <div key={question.id} className='border border-gray-200 rounded p-2'>
                      <div className='flex gap-2'>
                        <span className='flex-shrink-0 w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium'>
                          {index + 1}
                        </span>
                        <div className='flex-1 text-xs'>
                          <p className='font-medium text-gray-900 mb-1'>
                            {locale === 'rw'
                              ? question.questionKinyarwanda
                              : locale === 'fr'
                                ? question.questionFrench
                                : question.question}
                          </p>
                          {selectedOption ? (
                            <>
                              <div
                                className={`p-1.5 rounded ${
                                  selectedOption.marks > 0
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                }`}
                              >
                                <div className='flex justify-between items-start gap-2'>
                                  <span className='text-gray-900'>
                                    {locale === 'rw'
                                      ? selectedOption.optionKinyarwanda
                                      : locale === 'fr'
                                        ? selectedOption.optionFrench
                                        : selectedOption.option}
                                  </span>
                                  <div className='flex items-center gap-1'>
                                    <span className='flex-shrink-0 text-xs font-semibold'>
                                      {selectedOption.marks}/{question.weight}
                                    </span>
                                    {/* Show icon if this answer has subquestions */}
                                    {hasSubquestions && (
                                      <button
                                        type='button'
                                        onClick={() =>
                                          handleOpenSubquestionDialog(
                                            answer.selectedOptionId,
                                            locale === 'rw'
                                              ? question.questionKinyarwanda
                                              : locale === 'fr'
                                                ? question.questionFrench
                                                : question.question
                                          )
                                        }
                                        className='ml-1 p-0.5 text-primary hover:text-primary/80 hover:bg-secondary rounded transition-colors'
                                        title='View follow-up questions'
                                      >
                                        <MessageSquare className='h-3.5 w-3.5' />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className='p-1.5 rounded bg-gray-100'>
                              <span className='text-gray-500 italic'>No answer</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Score Summary */}
              <div className='mt-2 p-2 bg-gray-50 rounded border border-gray-200 flex justify-between items-center text-xs'>
                <span className='text-gray-600'>{dictionary['new_assessment.total_score']}</span>
                <span className='font-bold text-lg'>
                  {(() => {
                    let totalScore = 0;
                    let maxScore = 0;

                    // Calculate main questions score
                    questionnaire?.questions?.forEach(q => {
                      maxScore += q.weight;
                      const answer = answers.find(a => a.questionId === q.id);
                      if (answer) {
                        const option = q.facilityAssessmentQuestionOptions?.find(
                          opt => opt.id === answer.selectedOptionId
                        );
                        if (option) {
                          totalScore += option.marks;
                        }
                      }
                    });

                    return maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
                  })()}
                  %
                </span>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Compliance (review, Compliance assessment only) */}
          {assessmentType === 'COMPLIANCE' && complianceQuestions.length > 0 && (
            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-semibold'>
                  {dictionary['new_assessment.compliance_section_title'] || 'Compliance'}
                </CardTitle>
              </CardHeader>
              <CardContent className='pt-2'>
                <div className='space-y-6'>
                  {(() => {
                    const selectedOptionIds = buildSelectedOptionIds(complianceAnswers);
                    return (
                      <>
                        {complianceRootsBySection.orderedSections.map(section => {
                          const qs = complianceRootsBySection.groupedBySection[section.id] || [];
                          if (qs.length === 0) return null;
                          const visibleQs = qs.filter(q => isComplianceQuestionTreeVisible(q, selectedOptionIds));
                          if (visibleQs.length === 0) return null;
                          const sectionTitle =
                            locale === 'rw'
                              ? section.titleKinyarwanda || section.title
                              : locale === 'fr'
                                ? section.titleFrench || section.title
                                : section.title;
                          return (
                            <div key={section.id} className='space-y-2'>
                              <div className='border-b border-gray-200 pb-1'>
                                <h4 className='text-xs font-semibold text-gray-700'>{sectionTitle}</h4>
                              </div>
                              <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'>
                                {visibleQs.map(q => renderComplianceReviewCard(q))}
                              </div>
                            </div>
                          );
                        })}
                        {complianceRootsBySection.unsectionedRoots.filter(q =>
                          isComplianceQuestionTreeVisible(q, selectedOptionIds)
                        ).length > 0 && (
                          <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'>
                            {complianceRootsBySection.unsectionedRoots
                              .filter(q => isComplianceQuestionTreeVisible(q, selectedOptionIds))
                              .map(q => renderComplianceReviewCard(q))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assessment Photos */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.photos_count']} ({uploadedImages.length})
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <PhotoGallery />
            </CardContent>
          </Card>

          {/* Previous Assessment */}
          {(previousAssessment || (pendingRecommendations?.recommendations?.length ?? 0) > 0) && (
            <Card>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-semibold'>
                  {dictionary['new_assessment.previous_assessment']}
                </CardTitle>
              </CardHeader>
              <CardContent className='pt-2 space-y-2'>
                {previousAssessment && (
                  <div className='grid grid-cols-2 md:grid-cols-4 gap-2 text-xs'>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_score']} </span>
                      <span className='font-semibold'>{previousAssessment.percentageScore.toFixed(1)}%</span>
                    </div>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_decision']} </span>
                      <span className='font-semibold'>{previousAssessment.finalDecision}</span>
                    </div>
                    <div>
                      <span className='text-gray-500'>{dictionary['new_assessment.previous_date']} </span>
                      <span className='font-semibold'>{new Date(previousAssessment.createdAt).toLocaleDateString()}</span>
                    </div>
                    {previousAssessment.carriedBy && (
                      <div>
                        <span className='text-gray-500'>{dictionary['new_assessment.previous_by']} </span>
                        <span className='font-semibold'>{previousAssessment.carriedBy.name}</span>
                      </div>
                    )}
                  </div>
                )}
                {pendingRecommendations?.recommendations?.length ? (
                  <div className='text-xs pt-1 border-t border-gray-100'>
                    <span className='text-gray-500 font-medium block mb-1'>
                      {dictionary['new_assessment.previous_recommendations'] ?? 'Pending recommendations'}
                    </span>
                    <ul className='space-y-2 text-gray-700'>
                      {pendingRecommendations.recommendations.map(rec => (
                        <li key={rec.id} className={`flex flex-wrap items-start justify-between gap-2 ${rec.resolved ? 'opacity-70' : ''}`}>
                          <span className={rec.resolved ? 'line-through text-gray-500' : ''}>
                            {rec.recommendation}
                            {rec.deadline && (
                              <span className='text-gray-500 ml-1'>
                                ({dictionary['new_assessment.deadline'] ?? 'Deadline'}: {new Date(rec.deadline).toLocaleDateString()})
                              </span>
                            )}
                          </span>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            className='flex-shrink-0 text-xs'
                            disabled={updateRecommendationResolvedMutation.isPending}
                            onClick={() =>
                              updateRecommendationResolvedMutation.mutate({
                                recommendationId: rec.id,
                                resolved: !rec.resolved,
                              })
                            }
                          >
                            {rec.resolved
                              ? (dictionary['new_assessment.mark_not_resolved'] ?? 'Mark not resolved')
                              : (dictionary['new_assessment.mark_resolved'] ?? 'Mark resolved')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Committee Members */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.committee_members']}
                <span className='ml-2 text-xs font-normal text-red-500'>({dictionary['new_assessment.committee_min'] || 'min. 2 required'})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <div className='space-y-2'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
                  <Input
                    type='text'
                    placeholder={dictionary['new_assessment.member_name']}
                    value={newMemberName}
                    onChange={e => setNewMemberName(e.target.value)}
                    className='text-xs'
                  />
                  <Input
                    type='text'
                    inputMode='numeric'
                    autoComplete='tel'
                    maxLength={10}
                    placeholder={dictionary['new_assessment.member_phone']}
                    value={newMemberPhone}
                    onChange={e => setNewMemberPhone(sanitizeCommitteePhone(e.target.value))}
                    className='text-xs'
                  />
                  <Input
                    type='text'
                    placeholder={dictionary['new_assessment.member_position']}
                    value={newMemberPosition}
                    onChange={e => setNewMemberPosition(e.target.value)}
                    className='text-xs'
                  />
                </div>
                <Button
                  type='button'
                  onClick={() => {
                    if (newMemberName.trim()) {
                      setCommitteeMembers([
                        ...committeeMembers,
                        {
                          name: newMemberName.trim(),
                          phone: sanitizeCommitteePhone(newMemberPhone),
                          position: newMemberPosition.trim(),
                        },
                      ]);
                      setNewMemberName('');
                      setNewMemberPhone('');
                      setNewMemberPosition('');
                    }
                  }}
                  size='sm'
                  className='w-full text-xs'
                >
                  {dictionary['new_assessment.add_member']}
                </Button>
                {committeeMembers.length > 0 && (
                  <div className='space-y-1'>
                    {committeeMembers.map((member, index) => (
                      <div
                        key={index}
                        className='flex justify-between items-center p-2 bg-gray-50 rounded border border-gray-200 text-xs'
                      >
                        <div className='flex-1'>
                          <div className='font-medium'>{member.name}</div>
                          <div className='text-gray-600'>
                            {member.position} {member.phone && `• ${member.phone}`}
                          </div>
                        </div>
                        <button
                          type='button'
                          onClick={() => setCommitteeMembers(committeeMembers.filter((_, i) => i !== index))}
                          className='text-red-600'
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Flag for follow-up */}
          <Card>
            <CardContent className='pt-4'>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  {...assessmentRegister('isFlagged')}
                  className='rounded border-gray-300 text-amber-600 focus:ring-amber-500'
                />
                <span className='text-sm text-gray-700'>{dictionary['new_assessment.flag_for_followup'] ?? 'Flag this assessment for follow-up'}</span>
              </label>
            </CardContent>
          </Card>

          {/* Additional Comments */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.additional_comments']}
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              <textarea
                {...assessmentRegister('additionalComment')}
                rows={3}
                placeholder={dictionary['new_assessment.comments_placeholder']}
                className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary'
              />
            </CardContent>
          </Card>

          {/* Follow-up Date (optional) */}
          {/* Previous Assessment */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.previous_assessment'] || 'Previous Assessment'}
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-2'>
              {previousAssessment ? (
                <div className='space-y-2'>
                  {/* Main Info Grid */}
                  <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs'>
                    <div>
                      <span className='text-gray-500 block'>{dictionary['new_assessment.date'] || 'Date'}</span>
                      <span className='font-medium text-gray-900'>
                        {new Date(previousAssessment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 block'>{dictionary['new_assessment.score'] || 'Score'}</span>
                      <span className='font-medium text-gray-900'>
                        {previousAssessment.percentageScore.toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 block'>{dictionary['new_assessment.week'] || 'Week'}</span>
                      <span className='font-medium text-gray-900'>
                        W{previousAssessment.weekNumber}/{previousAssessment.year}
                      </span>
                    </div>
                    <div>
                      <span className='text-gray-500 block'>
                        {dictionary['new_assessment.inspector'] || 'Inspector'}
                      </span>
                      <span className='font-medium text-gray-900'>{previousAssessment.carriedBy?.name || '-'}</span>
                    </div>
                  </div>

                  {/* Decision */}
                  <div className='text-xs pt-1 border-t border-gray-100'>
                    <span className='text-gray-500'>
                      {dictionary['new_assessment.previous_decision'] || 'Decision:'}{' '}
                    </span>
                    <span
                      className={`font-medium ${
                        previousAssessment.finalDecision === 'COMPLIANT' ||
                        previousAssessment.finalDecision === 'NO_ACTION'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}
                    >
                      {previousAssessment.finalDecision === 'NO_ACTION'
                        ? dictionary['new_assessment.appreciation'] || 'Appreciation'
                        : previousAssessment.finalDecision.replace(/_/g, ' ')}
                    </span>
                    {previousAssessment.chargedAmount && previousAssessment.chargedAmount > 0 && (
                      <span className='ml-2'>
                        <span className='text-gray-500'>- </span>
                        <span className='font-medium text-gray-900'>
                          {previousAssessment.chargedAmount.toLocaleString()} RWF
                        </span>
                      </span>
                    )}
                  </div>

                  {/* Pending recommendations from previous assessment */}
                  {pendingRecommendations?.recommendations?.length ? (
                    <div className='text-xs pt-2 border-t border-gray-100'>
                      <span className='text-gray-500 font-medium block mb-1'>
                        {dictionary['new_assessment.previous_recommendations'] ?? 'Pending recommendations'}
                      </span>
                      <ul className='space-y-2 text-gray-700'>
                        {pendingRecommendations.recommendations.map(rec => (
                          <li key={rec.id} className={`flex flex-wrap items-start justify-between gap-2 ${rec.resolved ? 'opacity-70' : ''}`}>
                            <span className={rec.resolved ? 'line-through text-gray-500' : ''}>
                              {rec.recommendation}
                              {rec.deadline && (
                                <span className='text-gray-500 ml-1'>
                                  ({dictionary['new_assessment.deadline'] ?? 'Deadline'}: {new Date(rec.deadline).toLocaleDateString()})
                                </span>
                              )}
                            </span>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              className='flex-shrink-0 text-xs'
                              disabled={updateRecommendationResolvedMutation.isPending}
                              onClick={() =>
                                updateRecommendationResolvedMutation.mutate({
                                  recommendationId: rec.id,
                                  resolved: !rec.resolved,
                                })
                              }
                            >
                              {rec.resolved
                                ? (dictionary['new_assessment.mark_not_resolved'] ?? 'Mark not resolved')
                                : (dictionary['new_assessment.mark_resolved'] ?? 'Mark resolved')}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* Comment if exists */}
                  {previousAssessment.additionalComment && (
                    <div className='text-xs pt-1 border-t border-gray-100'>
                      <span className='text-gray-500 block mb-0.5'>
                        {dictionary['new_assessment.comment'] || 'Comment:'}
                      </span>
                      <span className='text-gray-700 italic'>"{previousAssessment.additionalComment}"</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className='text-xs text-gray-500'>
                  {dictionary['new_assessment.no_previous_assessment'] ||
                    'No previous assessment found for this facility'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pending Appeals from previous assessment */}
          {previousAssessment?.appeals && previousAssessment.appeals.filter(a => a.status === 'PENDING').length > 0 && (
            <Card className='border-amber-200 bg-amber-50'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-sm font-semibold text-amber-800'>
                  Pending Appeals
                </CardTitle>
                <p className='text-xs text-amber-700 font-normal'>
                  The facility has submitted appeals on the previous assessment. Resolve each one below.
                </p>
              </CardHeader>
              <CardContent className='pt-2 space-y-3'>
                {previousAssessment.appeals
                  .filter(a => a.status === 'PENDING')
                  .map(appeal => (
                    <div key={appeal.id} className='bg-white rounded-md border border-amber-200 p-3 space-y-2'>
                      <p className='text-xs text-gray-700'>{appeal.reason}</p>
                      <p className='text-xs text-gray-400'>Submitted {new Date(appeal.createdAt).toLocaleDateString()}</p>
                      <div className='flex gap-2'>
                        <button
                          type='button'
                          onClick={() => setAppealResolutions(prev => ({ ...prev, [appeal.id]: 'ACCEPTED' }))}
                          className={`flex-1 text-xs py-1.5 px-3 rounded border font-medium transition-colors ${
                            appealResolutions[appeal.id] === 'ACCEPTED'
                              ? 'bg-green-600 text-white border-green-600'
                              : 'border-green-300 text-green-700 hover:bg-green-50'
                          }`}
                        >
                          Accept
                        </button>
                        <button
                          type='button'
                          onClick={() => setAppealResolutions(prev => ({ ...prev, [appeal.id]: 'REJECTED' }))}
                          className={`flex-1 text-xs py-1.5 px-3 rounded border font-medium transition-colors ${
                            appealResolutions[appeal.id] === 'REJECTED'
                              ? 'bg-red-600 text-white border-red-600'
                              : 'border-red-300 text-red-700 hover:bg-red-50'
                          }`}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}

          {/* Decisions (multiple) */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.decisions'] ?? 'Decisions'} *
              </CardTitle>
              <p className='text-xs text-gray-500 font-normal'>
                {dictionary['new_assessment.decisions_hint'] ?? 'Select one or more outcomes.'}
              </p>
            </CardHeader>
            <CardContent className='pt-2 space-y-2'>
              <input type='hidden' {...assessmentRegister('finalDecision')} />
              <div className='flex flex-col gap-2'>
                {DECISION_OPTIONS.filter(opt => !opt.homeOnly || isHomeFacility).map(opt => (
                  <label
                    key={opt.value}
                    className='flex items-center gap-2 cursor-pointer text-xs'
                  >
                    <input
                      type='checkbox'
                      checked={selectedDecisions.includes(opt.value)}
                      onChange={() => {
                        const next = selectedDecisions.includes(opt.value)
                          ? selectedDecisions.filter(d => d !== opt.value)
                          : [...selectedDecisions.filter(d => d !== 'NO_ACTION'), opt.value];
                        if (next.length === 0) next.push('NO_ACTION');
                        setSelectedDecisions(next);
                      }}
                      className='rounded border-gray-300 text-primary focus:ring-primary'
                    />
                    <span>{dictionary[opt.labelKey] || opt.labelKey}</span>
                  </label>
                ))}
              </div>
              {selectedDecisions.length === 0 && (
                <p className='text-xs text-red-600'>
                  {dictionary['new_assessment.error_no_decision'] ?? 'Please select at least one decision'}
                </p>
              )}
              {selectedDecisions.includes('FINES') && (
                <div className='bg-yellow-50 border border-yellow-200 rounded p-2 mt-2'>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    {dictionary['new_assessment.charged_amount']} (RWF) *
                  </label>
                  <Input
                    type='text'
                    inputMode='numeric'
                    placeholder={dictionary['new_assessment.placeholder_fine']}
                    {...assessmentRegister('chargedAmount', {
                      valueAsNumber: true,
                    })}
                    min={0}
                    step={1}
                    className='w-full text-xs [&_input]:[-moz-appearance:textfield] [&_input::-webkit-outer-spin-button]:appearance-none [&_input::-webkit-inner-spin-button]:appearance-none'
                  />
                  {assessmentErrors.chargedAmount && (
                    <p className='mt-1 text-xs text-red-600'>{assessmentErrors.chargedAmount.message}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-semibold'>
                {dictionary['new_assessment.recommendations'] ?? 'Recommendations'}
              </CardTitle>
              <p className='text-xs text-gray-500 font-normal'>
                {dictionary['new_assessment.recommendations_hint'] ?? 'Optional. Add one or more recommendations with optional deadlines.'}
              </p>
            </CardHeader>
            <CardContent className='pt-2 space-y-3'>
              {recommendations.map((rec, idx) => (
                <div key={idx} className='flex flex-col gap-2 p-2 border border-gray-200 rounded bg-gray-50/50'>
                  <div className='flex gap-2'>
                    <Input
                      type='text'
                      value={rec.recommendation}
                      onChange={e => {
                        const next = [...recommendations];
                        next[idx] = { ...next[idx], recommendation: e.target.value };
                        setRecommendations(next);
                      }}
                      placeholder={dictionary['new_assessment.recommendation_placeholder'] ?? 'Recommendation text'}
                      className='flex-1 text-xs'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='text-xs shrink-0'
                      onClick={() => setRecommendations(recommendations.filter((_, i) => i !== idx))}
                    >
                      {dictionary['new_assessment.remove'] ?? 'Remove'}
                    </Button>
                  </div>
                  <div>
                    <label className='block text-xs text-gray-600 mb-0.5'>
                      {dictionary['new_assessment.deadline'] ?? 'Deadline'} (optional)
                    </label>
                    <Input
                      type='datetime-local'
                      value={
                        rec.deadline
                          ? new Date(new Date(rec.deadline).getTime() - new Date(rec.deadline).getTimezoneOffset() * 60000)
                              .toISOString()
                              .slice(0, 16)
                          : ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        const next = [...recommendations];
                        next[idx] = { ...next[idx], deadline: val ? new Date(val).toISOString() : undefined };
                        setRecommendations(next);
                      }}
                      className='text-xs'
                    />
                  </div>
                </div>
              ))}
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='text-xs'
                onClick={() => setRecommendations([...recommendations, { recommendation: '' }])}
              >
                {dictionary['new_assessment.add_recommendation'] ?? 'Add recommendation'}
              </Button>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className='flex justify-between gap-2 pt-3 pb-20 border-t border-gray-200 relative z-50'>
            <Button type='button' variant='outline' onClick={prevStep} size='sm' className='text-xs'>
              {dictionary['new_assessment.back_button']}
            </Button>
            <Button
              type='submit'
              disabled={createAssessmentMutation.isPending || isRestoringDraft}
              size='sm'
              className='text-xs relative z-50'
            >
              {createAssessmentMutation.isPending ? (
                <>
                  <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                  {dictionary['new_assessment.submitting']}
                </>
              ) : (
                dictionary['new_assessment.submit_assessment']
              )}
            </Button>
          </div>
        </form>
      )}

      <div className='flex justify-center pt-4'>
        <Button type='button' variant='outline' onClick={() => navigate('/assessments')}>
          {dictionary['new_assessment.cancel']}
        </Button>
      </div>

      {/* Subquestions Dialog for Review */}
      <Dialog
        open={subquestionDialog.isOpen}
        onOpenChange={open => setSubquestionDialog({ isOpen: open, optionId: '', questionText: '' })}
      >
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{dictionary['new_assessment.followup_questions_title']}</DialogTitle>
            <DialogDescription>
              {dictionary['new_assessment.followup_questions_desc']} {subquestionDialog.questionText}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-3 mt-4'>
            {loadingReviewSubquestions[subquestionDialog.optionId] ? (
              <div className='flex items-center justify-center py-8'>
                <div className='text-center'>
                  <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto mb-2' />
                  <p className='text-sm text-gray-600'>{dictionary['new_assessment.loading_followup']}</p>
                </div>
              </div>
            ) : reviewSubquestions[subquestionDialog.optionId] &&
              reviewSubquestions[subquestionDialog.optionId].length > 0 ? (
              reviewSubquestions[subquestionDialog.optionId].map((subQ, subIndex) => {
                const subAnswer = answers.find(a => a.questionId === subQ.id);
                const subSelectedOption = subQ.facilityAssessmentQuestionOptions?.find(
                  opt => opt.id === subAnswer?.selectedOptionId
                );

                return (
                  <div key={subQ.id} className='bg-secondary/30 border border-primary/30 rounded-lg p-3'>
                    <div className='mb-2'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span className='w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold'>
                          {subIndex + 1}
                        </span>
                        <p className='text-sm font-medium text-gray-900'>
                          {locale === 'rw'
                            ? subQ.questionKinyarwanda
                            : locale === 'fr'
                              ? subQ.questionFrench
                              : subQ.question}
                        </p>
                      </div>
                    </div>
                    <div className='ml-8'>
                      {subSelectedOption ? (
                        <div
                          className={`p-2 rounded text-sm border ${
                            subSelectedOption.marks > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className='flex justify-between items-start gap-2'>
                            <span className='text-gray-900'>
                              {locale === 'rw'
                                ? subSelectedOption.optionKinyarwanda
                                : locale === 'fr'
                                  ? subSelectedOption.optionFrench
                                  : subSelectedOption.option}
                            </span>
                            <span className='text-xs font-semibold'>
                              {subSelectedOption.marks}/{subQ.weight} pts
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className='p-2 rounded bg-gray-100 border border-gray-200'>
                          <span className='text-gray-500 italic text-sm'>
                            {dictionary['new_assessment.no_answer_provided']}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className='text-center py-8 text-gray-500'>
                <p>{dictionary['new_assessment.no_followup_available']}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateFacilityModal}
        onOpenChange={open => {
          if (!open && !createFacilityMutation.isPending) {
            handleCloseModal();
          }
        }}
      >
        <DialogContent
          className='max-w-2xl max-h-[90vh] overflow-y-auto'
          showCloseButton={!createFacilityMutation.isPending}
        >
          <DialogHeader>
            <DialogTitle>{dictionary['new_assessment.create_facility_modal_title']}</DialogTitle>
            <DialogDescription>
              {dictionary['new_assessment.create_facility_modal_desc'] || 'Create a new facility to add to the system'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {dictionary['facilities.facility_title']} *
              </label>
              <Input
                type='text'
                placeholder={dictionary['facilities.facility_title_placeholder']}
                {...facilityRegister('title')}
                className={facilityErrors.title ? 'border-red-300 ring-2 ring-red-100' : ''}
              />
              {facilityErrors.title && <p className='mt-1 text-sm text-red-600'>{facilityErrors.title.message}</p>}
            </div>

            {!isCreatingHomeFacility && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['facilities.identifier_number']} *
                </label>
                <Input
                  type='text'
                  placeholder={dictionary['facilities.identifier_placeholder']}
                  {...facilityRegister('identifierNumber')}
                  className={facilityErrors.identifierNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {facilityErrors.identifierNumber && (
                  <p className='mt-1 text-sm text-red-600'>{facilityErrors.identifierNumber.message}</p>
                )}
              </div>
            )}

            {/* Hidden input to store identifier for HOME facility type */}
            {isCreatingHomeFacility && (
              <input type='hidden' {...facilityRegister('identifierNumber')} value={watchedNidNumber || ''} />
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['facilities.contact_number']}
                </label>
                <Input
                  type='tel'
                  placeholder={dictionary['facilities.contact_placeholder']}
                  {...facilityRegister('contactNumber')}
                  className={facilityErrors.contactNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {facilityErrors.contactNumber && (
                  <p className='mt-1 text-sm text-red-600'>{facilityErrors.contactNumber.message}</p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  {dictionary['facilities.nid_number']}
                  {isCreatingHomeFacility && <span className='text-red-600 ml-1'>*</span>}
                </label>
                <Input
                  type='text'
                  placeholder={dictionary['facilities.nid_placeholder']}
                  {...facilityRegister('nidNumber')}
                  maxLength={16}
                  className={facilityErrors.nidNumber ? 'border-red-300 ring-2 ring-red-100' : ''}
                />
                {facilityErrors.nidNumber && (
                  <p className='mt-1 text-sm text-red-600'>{facilityErrors.nidNumber.message}</p>
                )}
                {isCreatingHomeFacility && (
                  <p className='mt-1 text-xs text-gray-500'>
                    {dictionary['facilities.nid_required_16'] || 'Required: Must be exactly 16 digits'}
                  </p>
                )}
                {!isCreatingHomeFacility && (
                  <p className='mt-1 text-xs text-gray-500'>
                    {dictionary['facilities.nid_optional_16'] || 'Optional: Must be 16 digits if provided'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {dictionary['facilities.location_selection']}
              </label>
              <SingleLocationSelector
                selectedLocationId={selectedLocationId}
                onLocationChange={id => setSelectedLocationId(id || '')}
                initialHierarchy={
                  locationHierarchy
                    ? {
                        provinceId: locationHierarchy.provinceId,
                        districtId: locationHierarchy.districtId,
                        sectorId: locationHierarchy.sectorId,
                        cellId: locationHierarchy.cellId,
                        villageId: locationHierarchy.villageId,
                      }
                    : undefined
                }
                allowChildrenOnly={true}
                disabled={!!locationHierarchy?.villageId}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Facility Type</label>
              {selectedFacilityTypeId ? (
                <div className='px-3 py-2 border border-green-200 rounded-lg bg-green-50'>
                  <div className='flex items-center gap-2'>
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                    <p className='text-green-900 font-medium'>
                      {selectedFacilityTypeId && facilityTypes.find(ft => ft.id === selectedFacilityTypeId)
                        ? getLocalizedFacilityTypeName(
                            facilityTypes.find(ft => ft.id === selectedFacilityTypeId)!,
                            locale
                          )
                        : dictionary['new_assessment.selected_type']}
                    </p>
                  </div>
                  <p className='text-xs text-green-700 mt-1'>
                    This matches the facility type you selected for the assessment
                  </p>
                </div>
              ) : (
                <FacilityTypeSelector
                  selectedFacilityTypeId={selectedFacilityTypeId}
                  onFacilityTypeChange={setSelectedFacilityTypeId}
                  className='mt-2'
                />
              )}
            </div>

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={handleCloseModal}
                disabled={createFacilityMutation.isPending}
              >
                {dictionary['new_assessment.cancel_button']}
              </Button>
              <Button
                type='button'
                onClick={handleFacilitySubmit(onFacilitySubmit)}
                disabled={createFacilityMutation.isPending}
              >
                {createFacilityMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Creating...
                  </>
                ) : (
                  dictionary['new_assessment.create_facility_button']
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
